import React, { useState, useEffect, useMemo, useCallback } from 'react';
import DashboardLayout from '../../../../layouts/DashboardLayout';
import { datamartApi } from '../../api/datamart.api';
import {
    FaDatabase,
    FaPlay,
    FaSpinner,
    FaTable,
    FaColumns,
    FaKey,
    FaLink,
    FaSearch,
    FaEye,
    FaDownload,
    FaSync,
    FaLayerGroup,
    FaTerminal,
    FaTimes,
    FaServer,
    FaInfoCircle,
    FaRobot,
    FaShieldAlt,
    FaPlus,
    FaEdit,
    FaTrash,
    FaHistory,
    FaToggleOn,
    FaToggleOff,
    FaFilter,
    FaCheckCircle,
    FaBan,
    FaCode,
    FaChevronDown,
    FaChevronRight,
    FaEraser,
    FaCopy,
    FaUsers,
    FaFileAlt,
    FaBuilding,
    FaListAlt,
    FaBolt,
    FaClock,
    FaMemory,
    FaNetworkWired,
    FaExclamationTriangle,
    FaArrowLeft
} from 'react-icons/fa';
import { SiRedis } from 'react-icons/si';
import { toast } from 'react-hot-toast';
import styles from '../../styles/Datamart.module.css';
import ResponsiveDictionary from '../../components/ResponsiveDictionary';

const RESPONSIVE_DICTIONARY_QUERY = '(max-width: 1024px)';

const matchesResponsiveDictionary = () => (
    typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia(RESPONSIVE_DICTIONARY_QUERY).matches
);

// ─── Predefined views displayed as visual cards ──────────────────────────────
const PREDEFINED_VIEWS = [
    {
        id: 'users',
        label: 'Usuarios del Sistema',
        description: 'Registro completo de cuentas de usuario, roles y credenciales de acceso.',
        db: 'identity_db',
        table: 'users',
        icon: FaUsers,
        color: '#6366F1',
        bg: '#EEF2FF'
    },
    {
        id: 'business_registry',
        label: 'Registro Empresarial',
        description: 'Padrón unificado de empresas y personas morales en emisiones.',
        db: 'emisiones_db',
        table: 'business_registry',
        icon: FaBuilding,
        color: '#0891B2',
        bg: '#ECFEFF'
    },
    {
        id: 'property_registry',
        label: 'Registro Patrimonial',
        description: 'Padrón catastral y situación patrimonial unificado en emisiones.',
        db: 'emisiones_db',
        table: 'property_registry',
        icon: FaListAlt,
        color: '#D97706',
        bg: '#FFFBEB'
    },
    {
        id: 'citizen_registry',
        label: 'Padrón Ciudadano (RENAPO)',
        description: 'Validación demográfica y registro de ciudadanos en emisiones.',
        db: 'emisiones_db',
        table: 'external_renapo_mock',
        icon: FaUsers,
        color: '#8B5CF6',
        bg: '#F5F3FF'
    },
    {
        id: 'emitted_certificates',
        label: 'Certificados Emitidos',
        description: 'Historial de certificados electrónicos oficiales emitidos y su vigencia.',
        db: 'validations_db',
        table: 'emitted_certificates',
        icon: FaFileAlt,
        color: '#059669',
        bg: '#ECFDF5'
    }
];

const VIEW_SUMMARY_FIELDS = ['name', 'nombre', 'title', 'titulo', 'label', 'username', 'email', 'rfc', 'id', 'uuid'];

const formatViewValue = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
};

const getViewRecordFields = (record) => (
    record && typeof record === 'object' && !Array.isArray(record)
        ? Object.entries(record)
        : [['Registro', record]]
);

const getViewRecordSummaryFields = (fields) => {
    const prioritizedFields = VIEW_SUMMARY_FIELDS
        .map((summaryField) => fields.find(([name]) => name.toLowerCase() === summaryField))
        .filter(Boolean);
    const summary = [...prioritizedFields];

    fields.forEach((field) => {
        if (summary.length < 3 && !summary.includes(field)) summary.push(field);
    });

    return summary.slice(0, 3);
};

// ─── Operators available for the Query Builder ───────────────────────────────
const OPERATORS = [
    { value: '=', label: '= Igual' },
    { value: '!=', label: '≠ Diferente' },
    { value: '>', label: '> Mayor que' },
    { value: '<', label: '< Menor que' },
    { value: '>=', label: '≥ Mayor o igual' },
    { value: '<=', label: '≤ Menor o igual' },
    { value: 'ILIKE', label: '≈ Contiene (texto)' },
    { value: 'IS NULL', label: '∅ Es nulo' },
    { value: 'IS NOT NULL', label: '≠∅ No es nulo' },
    { value: 'IS TRUE', label: '✓ Es verdadero' },
    { value: 'IS FALSE', label: '✗ Es falso' },
];

const NO_VALUE_OPS = ['IS NULL', 'IS NOT NULL', 'IS TRUE', 'IS FALSE'];

const REDIS_RESPONSIVE_SECTIONS = [
    { id: 'dictionary', label: 'Diccionario', icon: SiRedis },
    { id: 'live_keys', label: 'Claves en vivo', icon: FaBolt },
    { id: 'governance', label: 'Gobernanza', icon: FaShieldAlt }
];

const BUILDER_RESPONSIVE_STAGES = [
    { id: 'data', label: 'Datos' },
    { id: 'query', label: 'Consulta' },
    { id: 'ai', label: 'Acceso IA' }
];

// ─── Initial Query Builder state ──────────────────────────────────────────────
const initialBuilderState = {
    selectedDb: '',
    selectedTable: '',
    selectedColumns: [],   // [] = todas las columnas (SELECT *)
    filters: [],           // [{ id, column, operator, value }]
    limit: 50,
    offset: 0
};

function Datamart() {
    const [activeTab, setActiveTab] = useState('dictionary');

    // Data dictionary state.
    const [dictionaries, setDictionaries] = useState([]);
    const [loadingDict, setLoadingDict] = useState(false);
    const [selectedDbName, setSelectedDbName] = useState('');
    const [selectedTableName, setSelectedTableName] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [hideSystemTables, setHideSystemTables] = useState(true);
    const [isResponsiveDictionary, setIsResponsiveDictionary] = useState(matchesResponsiveDictionary);

    // AI access control state (datamart_db).
    const [aiRules, setAiRules] = useState([]);
    const [loadingAiRules, setLoadingAiRules] = useState(false);
    const [togglingRuleId, setTogglingRuleId] = useState(null);

    // Active context while the Query Builder creates or edits an AI rule.
    const [builderAiContext, setBuilderAiContext] = useState(null);
    // Inline AI configuration for the dynamic builder.
    const [builderAiForm, setBuilderAiForm] = useState({
        name: '',
        code: '',
        description: '',
        is_enabled: true
    });
    // Modal for linking the generated query to AI.
    const [saveAiModalOpen, setSaveAiModalOpen] = useState(false);
    const [saveAiForm, setSaveAiForm] = useState({
        name: '',
        code: '',
        description: '',
        is_enabled: true
    });
    const [saveAiLoading, setSaveAiLoading] = useState(false);

    // AI query test modal.
    const [aiTestModal, setAiTestModal] = useState({
        isOpen: false,
        ruleName: '',
        queryCode: '',
        data: [],
        loading: false,
        error: null
    });

    // AI audit log modal.
    const [auditModalOpen, setAuditModalOpen] = useState(false);
    const [auditLogs, setAuditLogs] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(false);

    // Sample preview state (dictionary modal).
    const [sampleModal, setSampleModal] = useState({
        isOpen: false,
        db: '',
        table: '',
        data: [],
        loading: false,
        error: null
    });

    // Redis data dictionary state (cache and rate limits).
    const [redisData, setRedisData] = useState(null);
    const [loadingRedis, setLoadingRedis] = useState(false);
    const [redisSearch, setRedisSearch] = useState('');
    const [redisServiceFilter, setRedisServiceFilter] = useState('ALL');
    const [activeRedisSection, setActiveRedisSection] = useState('dictionary');
    const [expandedNamespaces, setExpandedNamespaces] = useState({});
    const [redisLiveKeys, setRedisLiveKeys] = useState([]);
    const [loadingLiveKeys, setLoadingLiveKeys] = useState(false);
    const [liveKeyPattern, setLiveKeyPattern] = useState('*');
    const [selectedLiveKey, setSelectedLiveKey] = useState(null);
    const [liveKeyDetailModal, setLiveKeyDetailModal] = useState(false);

    // ─── Predefined views state ─────────────────────────────────────────────
    const [selectedView, setSelectedView] = useState(null);
    const [viewsData, setViewsData] = useState([]);
    const [loadingView, setLoadingView] = useState(false);
    const [viewError, setViewError] = useState(null);

    // ─── Visual Query Builder state ─────────────────────────────────────────
    const [builder, setBuilder] = useState(initialBuilderState);
    const [activeBuilderStage, setActiveBuilderStage] = useState('data');
    const [queryData, setQueryData] = useState([]);
    const [queryLoading, setQueryLoading] = useState(false);
    const [showSqlPreview, setShowSqlPreview] = useState(true);
    const [filterIdCounter, setFilterIdCounter] = useState(0);

    // Shared loading state.
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (typeof window.matchMedia !== 'function') return undefined;

        const mediaQuery = window.matchMedia(RESPONSIVE_DICTIONARY_QUERY);
        const updateResponsiveLayout = (event) => setIsResponsiveDictionary(event.matches);
        mediaQuery.addEventListener('change', updateResponsiveLayout);

        return () => mediaQuery.removeEventListener('change', updateResponsiveLayout);
    }, []);

    const fetchDictionaries = useCallback(async () => {
        setLoadingDict(true);
        try {
            const data = await datamartApi.getDictionary();
            if (Array.isArray(data)) {
                setDictionaries(data);
                if (data.length > 0) {
                    const firstWithTables = data.find(d => d.tables && d.tables.length > 0) || data[0];
                    setSelectedDbName(prev => prev || firstWithTables.name);
                    if (firstWithTables.tables && firstWithTables.tables.length > 0) {
                        const firstTable = firstWithTables.tables.find(t => !t.is_system) || firstWithTables.tables[0];
                        setSelectedTableName(prev => prev || firstTable.name);
                    }
                }
            }
        } catch (error) {
            toast.error(error.message || "Error al cargar el diccionario de datos.");
        } finally {
            setLoadingDict(false);
        }
    }, []);

    const fetchAiRules = useCallback(async () => {
        setLoadingAiRules(true);
        try {
            const data = await datamartApi.getAiRules();
            if (Array.isArray(data)) {
                setAiRules(data);
            }
        } catch (error) {
            console.error("Error al cargar reglas de IA:", error);
        } finally {
            setLoadingAiRules(false);
        }
    }, []);

    const fetchRedisDictionary = useCallback(async () => {
        setLoadingRedis(true);
        try {
            const data = await datamartApi.getRedisDictionary();
            if (data) {
                setRedisData(data);
            }
        } catch (error) {
            console.error("Error al cargar diccionario Redis:", error);
            toast.error("No se pudo cargar el diccionario de Redis.");
        } finally {
            setLoadingRedis(false);
        }
    }, []);

    const fetchRedisLiveKeys = useCallback(async (pattern) => {
        const queryPattern = pattern !== undefined ? pattern : liveKeyPattern;
        setLoadingLiveKeys(true);
        try {
            const data = await datamartApi.getRedisLiveKeys(queryPattern, 100);
            if (data && Array.isArray(data.keys)) {
                setRedisLiveKeys(data.keys);
            } else {
                setRedisLiveKeys([]);
            }
        } catch (error) {
            console.error("Error al inspeccionar claves en Redis:", error);
            toast.error("No se pudieron consultar las claves de Redis.");
        } finally {
            setLoadingLiveKeys(false);
        }
    }, [liveKeyPattern]);

    // Load dictionaries, AI rules, and the Redis dictionary on mount.
    useEffect(() => {
        fetchDictionaries();
        fetchAiRules();
        fetchRedisDictionary();
        fetchRedisLiveKeys('*');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reload Redis metrics when selecting the tab if they have not loaded yet.
    useEffect(() => {
        if (activeTab === 'redis' && !redisData && !loadingRedis) {
            fetchRedisDictionary();
            fetchRedisLiveKeys();
        }
    }, [activeTab, redisData, loadingRedis, fetchRedisDictionary, fetchRedisLiveKeys]);

    // Sync the dynamic builder's AI form when the table or context changes.
    useEffect(() => {
        if (builder.selectedTable) {
            const prettyName = builder.selectedTable.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            const prettyCode = builder.selectedTable.toLowerCase();
            setBuilderAiForm(prev => ({
                name: builderAiContext?.name || prev.name || prettyName,
                code: builderAiContext?.code || prev.code || prettyCode,
                description: builderAiContext?.description !== undefined ? builderAiContext.description : prev.description,
                is_enabled: builderAiContext?.is_enabled !== undefined ? builderAiContext.is_enabled : (prev.is_enabled !== undefined ? prev.is_enabled : true)
            }));
        }
    }, [builder.selectedTable, builderAiContext]);

    const handleToggleNamespaceExpand = (id, isExpanded) => {
        setExpandedNamespaces(prev => ({
            ...prev,
            [id]: !isExpanded
        }));
    };

    const handleRedisSubtabKeyDown = (event, currentSection) => {
        const currentIndex = REDIS_RESPONSIVE_SECTIONS.findIndex(section => section.id === currentSection);
        let nextIndex = currentIndex;

        if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % REDIS_RESPONSIVE_SECTIONS.length;
        else if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + REDIS_RESPONSIVE_SECTIONS.length) % REDIS_RESPONSIVE_SECTIONS.length;
        else if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = REDIS_RESPONSIVE_SECTIONS.length - 1;
        else return;

        event.preventDefault();
        const nextSection = REDIS_RESPONSIVE_SECTIONS[nextIndex];
        setActiveRedisSection(nextSection.id);
        document.getElementById(`redis-responsive-tab-${nextSection.id}`)?.focus();
    };

    const handleBuilderStageKeyDown = (event, currentStage) => {
        const enabledStages = BUILDER_RESPONSIVE_STAGES.filter(stage =>
            stage.id === 'data' || Boolean(builder.selectedTable)
        );
        const currentIndex = enabledStages.findIndex(stage => stage.id === currentStage);
        let nextIndex = currentIndex;

        if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % enabledStages.length;
        else if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + enabledStages.length) % enabledStages.length;
        else if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = enabledStages.length - 1;
        else return;

        const nextStage = enabledStages[nextIndex];

        event.preventDefault();
        setActiveBuilderStage(nextStage.id);
        document.getElementById(`qb-stage-tab-${nextStage.id}`)?.focus();
    };

    const handleCopyCommand = (cmdText) => {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(cmdText);
            toast.success("Copiado al portapapeles");
        }
    };

    const handleExportRedisJson = () => {
        try {
            const payload = {
                ...redisData,
                exported_at: new Date().toISOString(),
                live_keys_snapshot: redisLiveKeys
            };
            const jsonStr = JSON.stringify(payload, null, 2);
            const blob = new Blob([jsonStr], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `diccionario_redis_${new Date().toISOString().slice(0, 10)}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success("Diccionario de Redis exportado exitosamente.");
        } catch (err) {
            toast.error("Error al exportar el diccionario de Redis.");
        }
    };

    const filteredRedisNamespaces = useMemo(() => {
        if (!redisData || !Array.isArray(redisData.namespaces)) return [];
        return redisData.namespaces.filter(ns => {
            const matchesService = redisServiceFilter === 'ALL' || ns.owner_service === redisServiceFilter;
            const searchLower = redisSearch.toLowerCase().trim();
            const matchesSearch = !searchLower ||
                ns.namespace.toLowerCase().includes(searchLower) ||
                ns.name.toLowerCase().includes(searchLower) ||
                ns.description.toLowerCase().includes(searchLower) ||
                ns.owner_service.toLowerCase().includes(searchLower) ||
                ns.category.toLowerCase().includes(searchLower);
            return matchesService && matchesSearch;
        });
    }, [redisData, redisServiceFilter, redisSearch]);


    const fetchAuditLogs = async () => {
        setLoadingLogs(true);
        try {
            const logs = await datamartApi.getAiAuditLogs(50);
            setAuditLogs(logs || []);
            setAuditModalOpen(true);
        } catch (error) {
            toast.error("Error al obtener la bitácora de auditoría.");
        } finally {
            setLoadingLogs(false);
        }
    };

    // Toggle an AI rule on or off in real time.
    const handleToggleRule = async (rule) => {
        const nextState = !rule.is_enabled;
        setTogglingRuleId(rule.id);
        try {
            const updated = await datamartApi.toggleAiRule(rule.id, nextState);
            setAiRules(prev => prev.map(r => r.id === rule.id ? updated : r));
            if (nextState) {
                toast.success(`Acceso habilitado para '${rule.name}'. La IA puede consultar estos datos.`);
            } else {
                toast.error(`Acceso bloqueado para '${rule.name}'. La IA ya no puede acceder a estos datos.`);
            }
        } catch (error) {
            toast.error(error.message || "No se pudo actualizar el estado de la regla.");
        } finally {
            setTogglingRuleId(null);
        }
    };

    // Test an AI query to verify which data it receives.
    const handleTestAiQuery = async (rule) => {
        setAiTestModal({
            isOpen: true,
            ruleName: rule.name,
            queryCode: rule.code,
            data: [],
            loading: true,
            error: null
        });

        try {
            const resultData = await datamartApi.testAiRule(rule.id);
            setAiTestModal(prev => ({
                ...prev,
                data: resultData || [],
                loading: false
            }));
        } catch (err) {
            setAiTestModal(prev => ({
                ...prev,
                loading: false,
                error: err.message || "Error al ejecutar la consulta de prueba de IA."
            }));
        }
    };

    // Format WHERE filters for display in cards.
    const renderFilterRulesText = (filterRules) => {
        if (!filterRules) return 'Sin restricciones WHERE (Todos los registros)';
        if (Array.isArray(filterRules)) {
            const valid = filterRules.filter(f => f && f.column);
            if (valid.length === 0) return 'Sin restricciones WHERE (Todos los registros)';
            return valid.map(f => {
                if (NO_VALUE_OPS.includes(f.operator)) return `${f.column} ${f.operator}`;
                return `${f.column} ${f.operator || '='} ${f.value !== undefined ? JSON.stringify(f.value) : ''}`;
            }).join(' AND ');
        }
        if (typeof filterRules === 'object') {
            const entries = Object.entries(filterRules);
            if (entries.length === 0) return 'Sin restricciones WHERE (Todos los registros)';
            return entries.map(([k, v]) => {
                if (typeof v === 'object' && v !== null && v.operator) {
                    return `${k} ${v.operator} ${v.value !== undefined ? JSON.stringify(v.value) : ''}`;
                }
                return `${k} = ${JSON.stringify(v)}`;
            }).join(' AND ');
        }
        return String(filterRules);
    };

    // Parse an existing AI rule into the visual Query Builder.
    const parseFilterRulesToBuilder = (filterRules) => {
        if (!filterRules) return [];
        if (Array.isArray(filterRules)) {
            return filterRules.map((f, idx) => ({
                id: idx + 1,
                column: f.column || '',
                operator: f.operator || '=',
                value: f.value ?? ''
            }));
        }
        if (typeof filterRules === 'object') {
            let id = 1;
            const result = [];
            for (const [col, val] of Object.entries(filterRules)) {
                if (typeof val === 'object' && val !== null && val.operator) {
                    result.push({
                        id: id++,
                        column: col,
                        operator: val.operator,
                        value: val.value ?? ''
                    });
                } else {
                    result.push({
                        id: id++,
                        column: col,
                        operator: '=',
                        value: val
                    });
                }
            }
            return result;
        }
        return [];
    };

    // Start creating a new AI rule from the dynamic builder.
    const handleStartCreateAiInBuilder = () => {
        const defaultDb = dictionaries[0]?.name || '';
        const defaultTbl = dictionaries[0]?.tables?.find(t => !t.is_system)?.name || '';
        setBuilder({
            selectedDb: defaultDb,
            selectedTable: defaultTbl,
            selectedColumns: [],
            filters: [],
            limit: 100,
            offset: 0
        });
        setQueryData([]);
        setBuilderAiContext({
            isNew: true,
            ruleId: null,
            name: '',
            code: '',
            description: '',
            is_enabled: true
        });
        setActiveBuilderStage('data');
        setActiveTab('queries');
        toast.success("Constructor Dinámico activado: Configura la consulta que leerá la IA.");
    };

    // Load an existing AI rule into the dynamic builder for editing.
    const handleEditInBuilder = (rule) => {
        const parsedFilters = parseFilterRulesToBuilder(rule.filter_rules);
        const selCols = (Array.isArray(rule.selected_columns) && rule.selected_columns[0] !== '*')
            ? rule.selected_columns
            : [];

        setBuilder({
            selectedDb: rule.target_db,
            selectedTable: rule.target_table,
            selectedColumns: selCols,
            filters: parsedFilters,
            limit: rule.max_rows || 100,
            offset: 0
        });
        setFilterIdCounter(parsedFilters.length + 1);
        setQueryData([]);
        setBuilderAiContext({
            isNew: false,
            ruleId: rule.id,
            name: rule.name,
            code: rule.code,
            description: rule.description || '',
            is_enabled: rule.is_enabled
        });
        setActiveBuilderStage('data');
        setActiveTab('queries');
        toast.success(`Consulta de '${rule.name}' cargada en el Constructor Dinámico.`);
    };

    // Open the AI save modal from the Query Builder.
    const handleOpenSaveAiModal = () => {
        if (!builder.selectedDb || !builder.selectedTable) {
            toast.error("Selecciona una base de datos y tabla en el constructor antes de vincular.");
            return;
        }

        const defaultName = (builderAiForm.name || builderAiContext?.name || (
            builder.selectedTable
                ? builder.selectedTable.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                : ''
        )).trim();
        const defaultCode = (builderAiForm.code || builderAiContext?.code || (
            builder.selectedTable
                ? builder.selectedTable.toLowerCase()
                : ''
        )).trim();
        const defaultDesc = builderAiForm.description || builderAiContext?.description || '';
        const defaultEnabled = builderAiForm.is_enabled !== undefined
            ? builderAiForm.is_enabled
            : (builderAiContext?.is_enabled !== undefined ? builderAiContext.is_enabled : true);

        setSaveAiForm({
            name: defaultName,
            code: defaultCode,
            description: defaultDesc,
            is_enabled: defaultEnabled
        });
        setSaveAiModalOpen(true);
    };

    // Save a rule to datamart_db from the dynamic builder.
    const handleSaveAiRuleSubmit = async (e) => {
        e.preventDefault();
        setSaveAiLoading(true);
        try {
            const cols = builder.selectedColumns.length > 0 ? builder.selectedColumns : ['*'];
            const validFilters = builder.filters
                .filter(f => f.column)
                .map(f => {
                    let val = f.value;
                    if (val === 'true' || val === true) val = true;
                    else if (val === 'false' || val === false) val = false;
                    else if (!isNaN(val) && val !== '' && !NO_VALUE_OPS.includes(f.operator)) val = Number(val);
                    return {
                        column: f.column,
                        operator: f.operator,
                        value: NO_VALUE_OPS.includes(f.operator) ? null : val
                    };
                });

            const ruleCode = saveAiForm.code.trim().toLowerCase().replace(/\s+/g, '_');
            const payload = {
                code: ruleCode,
                name: saveAiForm.name.trim(),
                description: saveAiForm.description.trim(),
                target_db: builder.selectedDb,
                target_table: builder.selectedTable,
                selected_columns: cols,
                filter_rules: validFilters,
                is_enabled: saveAiForm.is_enabled,
                max_rows: builder.limit || 100
            };

            const targetRuleId = (builderAiContext && !builderAiContext.isNew && builderAiContext.ruleId)
                ? builderAiContext.ruleId
                : aiRules.find(r => r.code === ruleCode)?.id;

            if (targetRuleId) {
                const updated = await datamartApi.updateAiRule(targetRuleId, payload);
                setAiRules(prev => prev.map(r => r.id === targetRuleId ? updated : r));
                toast.success(`Configuración de consulta '${payload.name}' actualizada para la IA. (${payload.is_enabled ? 'Habilitada para chat' : 'Bloqueada'})`);
            } else {
                const created = await datamartApi.createAiRule(payload);
                setAiRules(prev => [...prev, created]);
                toast.success(`Configuración de consulta guardada para la IA en datamart_db. (${payload.is_enabled ? 'Habilitada para chat' : 'Bloqueada'})`);
            }

            setSaveAiModalOpen(false);
            setBuilderAiContext(null);
            setActiveTab('ai_control');
        } catch (err) {
            toast.error(err.message || "Error al guardar la regla para la IA.");
        } finally {
            setSaveAiLoading(false);
        }
    };

    // Save the AI query configuration directly from the builder.
    const handleSaveAiConfigDirect = async () => {
        if (!builder.selectedDb || !builder.selectedTable) {
            toast.error("Selecciona una base de datos y tabla primero en el constructor.");
            return;
        }
        setSaveAiLoading(true);
        try {
            const cols = builder.selectedColumns.length > 0 ? builder.selectedColumns : ['*'];
            const validFilters = builder.filters
                .filter(f => f.column)
                .map(f => {
                    let val = f.value;
                    if (val === 'true' || val === true) val = true;
                    else if (val === 'false' || val === false) val = false;
                    else if (!isNaN(val) && val !== '' && !NO_VALUE_OPS.includes(f.operator)) val = Number(val);
                    return {
                        column: f.column,
                        operator: f.operator,
                        value: NO_VALUE_OPS.includes(f.operator) ? null : val
                    };
                });

            const ruleCode = (builderAiForm.code || builder.selectedTable).trim().toLowerCase().replace(/\s+/g, '_');
            const ruleName = (builderAiForm.name || builder.selectedTable.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())).trim();

            const payload = {
                code: ruleCode,
                name: ruleName,
                description: (builderAiForm.description || '').trim(),
                target_db: builder.selectedDb,
                target_table: builder.selectedTable,
                selected_columns: cols,
                filter_rules: validFilters,
                is_enabled: builderAiForm.is_enabled,
                max_rows: builder.limit || 100
            };

            const targetRuleId = (builderAiContext && !builderAiContext.isNew && builderAiContext.ruleId)
                ? builderAiContext.ruleId
                : aiRules.find(r => r.code === ruleCode)?.id;

            if (targetRuleId) {
                const updated = await datamartApi.updateAiRule(targetRuleId, payload);
                setAiRules(prev => prev.map(r => r.id === targetRuleId ? updated : r));
                toast.success(`Configuración de consulta '${payload.name}' actualizada para la IA. (${payload.is_enabled ? 'Habilitada para chat' : 'Bloqueada'})`);
            } else {
                const created = await datamartApi.createAiRule(payload);
                setAiRules(prev => [...prev, created]);
                toast.success(`Configuración de consulta guardada para la IA en datamart_db. (${payload.is_enabled ? 'Habilitada para chat' : 'Bloqueada'})`);
            }

            setBuilderAiContext(null);
            setSaveAiModalOpen(false);
        } catch (err) {
            toast.error(err.message || "Error al guardar la configuración de consulta para la IA.");
        } finally {
            setSaveAiLoading(false);
        }
    };

    const handleDeleteRule = async (ruleId) => {
        if (!window.confirm("¿Estás seguro de eliminar esta regla de acceso para la IA?")) return;
        try {
            await datamartApi.deleteAiRule(ruleId);
            setAiRules(prev => prev.filter(r => r.id !== ruleId));
            toast.success("Regla eliminada del catálogo.");
        } catch (err) {
            toast.error(err.message || "No se pudo eliminar la regla.");
        }
    };

    // Currently selected database.
    const currentDb = useMemo(() => {
        return dictionaries.find(d => d.name === selectedDbName) || null;
    }, [dictionaries, selectedDbName]);

    // Tables filtered from the current database.
    const currentTables = useMemo(() => {
        if (!currentDb || !currentDb.tables) return [];
        return currentDb.tables.filter(t => {
            if (hideSystemTables && t.is_system) return false;
            return true;
        });
    }, [currentDb, hideSystemTables]);

    // Currently selected table.
    const currentTable = useMemo(() => {
        if (!currentTables || currentTables.length === 0) return null;
        return currentTables.find(t => t.name === selectedTableName) || currentTables[0] || null;
    }, [currentTables, selectedTableName]);

    // Global dictionary metrics.
    const globalMetrics = useMemo(() => {
        const totalDbs = dictionaries.length;
        let totalTables = 0;
        let totalCols = 0;
        dictionaries.forEach(d => {
            const validTables = (d.tables || []).filter(t => !hideSystemTables || !t.is_system);
            totalTables += validTables.length;
            validTables.forEach(t => {
                totalCols += (t.columns || []).length;
            });
        });
        return { totalDbs, totalTables, totalCols };
    }, [dictionaries, hideSystemTables]);

    // Filter by global or local search.
    const filteredDatabases = useMemo(() => {
        if (!searchQuery.trim()) return dictionaries;
        const q = searchQuery.toLowerCase().trim();
        return dictionaries.filter(db => {
            if (db.name.toLowerCase().includes(q) || (db.title && db.title.toLowerCase().includes(q))) {
                return true;
            }
            return (db.tables || []).some(t => {
                if (t.name.toLowerCase().includes(q)) return true;
                return (t.columns || []).some(c => c.name.toLowerCase().includes(q));
            });
        });
    }, [dictionaries, searchQuery]);

    const responsiveDatabases = useMemo(() => {
        if (!selectedDbName || filteredDatabases.some(db => db.name === selectedDbName)) {
            return filteredDatabases;
        }
        const selectedDatabase = dictionaries.find(db => db.name === selectedDbName);
        return selectedDatabase ? [selectedDatabase, ...filteredDatabases] : filteredDatabases;
    }, [dictionaries, filteredDatabases, selectedDbName]);

    const handleSelectDb = (dbName) => {
        setSelectedDbName(dbName);
        const targetDb = dictionaries.find(d => d.name === dbName);
        if (targetDb && targetDb.tables && targetDb.tables.length > 0) {
            const firstTable = targetDb.tables.find(t => !hideSystemTables || !t.is_system) || targetDb.tables[0];
            setSelectedTableName(firstTable ? firstTable.name : '');
        } else {
            setSelectedTableName('');
        }
    };

    const handleOpenSample = async (dbName, tableName) => {
        setSampleModal({
            isOpen: true,
            db: dbName,
            table: tableName,
            data: [],
            loading: true,
            error: null
        });

        try {
            const sampleData = await datamartApi.getTableSample(dbName, tableName, 10);
            setSampleModal(prev => ({
                ...prev,
                data: sampleData,
                loading: false
            }));
        } catch (err) {
            setSampleModal(prev => ({
                ...prev,
                loading: false,
                error: err.message || "No se pudo obtener la muestra de datos."
            }));
            toast.error("Error al cargar la previsualización de datos.");
        }
    };

    // ─── Send a dictionary table to the Query Builder ────────────────────────
    const handleSendToQuery = (dbName, tableObj) => {
        if (!tableObj) return;
        setBuilder(prev => ({
            ...prev,
            selectedDb: dbName,
            selectedTable: tableObj.name,
            selectedColumns: [],
            filters: [],
            limit: 50,
            offset: 0
        }));
        setQueryData([]);
        setActiveBuilderStage('data');
        setActiveTab('queries');
        toast.success(`Query Builder cargado para ${dbName}.${tableObj.name}`);
    };

    const handleExportJson = () => {
        try {
            const jsonStr = JSON.stringify(dictionaries, null, 2);
            const blob = new Blob([jsonStr], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `diccionario_bases_datos_${new Date().toISOString().slice(0, 10)}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success("Diccionario exportado exitosamente.");
        } catch (err) {
            toast.error("Error al exportar el diccionario.");
        }
    };

    // ─── Predefined views: load by card ──────────────────────────────────────
    const handleFetchView = async (viewId) => {
        const view = PREDEFINED_VIEWS.find(v => v.id === viewId);
        if (!view) return;
        setSelectedView(viewId);
        setLoadingView(true);
        setViewError(null);
        setViewsData([]);
        try {
            const data = await datamartApi.getViews(viewId, 100, 0);
            setViewsData(data);
            if (data.length === 0) toast.success("La vista no contiene datos.");
        } catch (error) {
            const message = error.message || "Error al obtener la vista";
            setViewError(message);
            toast.error(message);
        } finally {
            setLoadingView(false);
        }
    };

    // ─── Visual Query Builder helpers ─────────────────────────────────────────
    const builderDb = useMemo(() =>
        dictionaries.find(d => d.name === builder.selectedDb) || null,
    [dictionaries, builder.selectedDb]);

    const builderTables = useMemo(() => {
        if (!builderDb) return [];
        return (builderDb.tables || []).filter(t => !t.is_system);
    }, [builderDb]);

    const builderTable = useMemo(() =>
        builderTables.find(t => t.name === builder.selectedTable) || null,
    [builderTables, builder.selectedTable]);

    const builderColumns = useMemo(() =>
        (builderTable?.columns || []),
    [builderTable]);

    const selectedColumnCount = builder.selectedColumns.length === 0
        ? builderColumns.length
        : builderColumns.filter(column => builder.selectedColumns.includes(column.name)).length;

    // Dynamically generated SQL preview.
    const sqlPreview = useMemo(() => {
        if (!builder.selectedDb || !builder.selectedTable) return '-- Selecciona una base de datos y tabla para comenzar';
        const selCols = builder.selectedColumns.length === 0
            ? '*'
            : builder.selectedColumns.join(', ');
        let sql = `SELECT ${selCols}\nFROM ${builder.selectedTable}`;
        if (builder.filters.length > 0) {
            const validFilters = builder.filters.filter(f => f.column);
            if (validFilters.length > 0) {
                const conditions = validFilters.map(f => {
                    if (NO_VALUE_OPS.includes(f.operator)) return `${f.column} ${f.operator}`;
                    const val = f.value === '' ? "''"
                        : isNaN(f.value) ? `'${f.value}'`
                        : f.value;
                    if (f.operator === 'ILIKE') return `${f.column} ILIKE '%${f.value}%'`;
                    return `${f.column} ${f.operator} ${val}`;
                });
                sql += `\nWHERE ${conditions.join('\n  AND ')}`;
            }
        }
        sql += `\nLIMIT ${builder.limit}`;
        if (builder.offset > 0) sql += ` OFFSET ${builder.offset}`;
        sql += ';';
        return sql;
    }, [builder]);

    const handleBuilderDbSelect = (dbName) => {
        setBuilder(prev => ({
            ...prev,
            selectedDb: dbName,
            selectedTable: '',
            selectedColumns: [],
            filters: []
        }));
        setQueryData([]);
    };

    const handleBuilderTableSelect = (tableName) => {
        setBuilder(prev => ({
            ...prev,
            selectedTable: tableName,
            selectedColumns: [],
            filters: []
        }));
        setQueryData([]);
    };

    const handleToggleColumn = (colName) => {
        setBuilder(prev => {
            const already = prev.selectedColumns.includes(colName);
            return {
                ...prev,
                selectedColumns: already
                    ? prev.selectedColumns.filter(c => c !== colName)
                    : [...prev.selectedColumns, colName]
            };
        });
    };

    const handleSelectAllColumns = () => {
        setBuilder(prev => ({ ...prev, selectedColumns: [] }));
    };

    const handleAddFilter = () => {
        const newId = filterIdCounter + 1;
        setFilterIdCounter(newId);
        setBuilder(prev => ({
            ...prev,
            filters: [
                ...prev.filters,
                { id: newId, column: builderColumns[0]?.name || '', operator: '=', value: '' }
            ]
        }));
    };

    const handleRemoveFilter = (filterId) => {
        setBuilder(prev => ({ ...prev, filters: prev.filters.filter(f => f.id !== filterId) }));
    };

    const handleFilterChange = (filterId, field, value) => {
        setBuilder(prev => ({
            ...prev,
            filters: prev.filters.map(f =>
                f.id === filterId ? { ...f, [field]: value } : f
            )
        }));
    };

    const handleRunBuilderQuery = async () => {
        if (!builder.selectedDb || !builder.selectedTable) {
            toast.error('Selecciona una base de datos y tabla primero.');
            return;
        }
        setQueryLoading(true);
        setQueryData([]);
        try {
            const whereObj = {};
            builder.filters.filter(f => f.column).forEach(f => {
                if (NO_VALUE_OPS.includes(f.operator)) {
                    whereObj[f.column] = { operator: f.operator };
                } else if (f.operator === 'ILIKE') {
                    whereObj[f.column] = { operator: 'ILIKE', value: f.value };
                } else {
                    whereObj[f.column] = isNaN(f.value) ? f.value : Number(f.value);
                }
            });
            const payload = {
                db: builder.selectedDb,
                table: builder.selectedTable,
                select: builder.selectedColumns.length > 0 ? builder.selectedColumns : null,
                where: Object.keys(whereObj).length > 0 ? whereObj : null,
                limit: builder.limit,
                offset: builder.offset
            };
            const data = await datamartApi.postQuery(payload);
            setQueryData(data);
            if (data.length === 0) {
                toast.success('La consulta no devolvió resultados.');
            } else {
                toast.success(`${data.length} registro(s) obtenidos correctamente.`);
            }
        } catch (error) {
            toast.error(error.message || 'Error al ejecutar la consulta');
        } finally {
            setQueryLoading(false);
        }
    };

    const handleCopySql = () => {
        navigator.clipboard.writeText(sqlPreview);
        toast.success('SQL copiado al portapapeles.');
    };

    // Legacy handleRunQuery for backward compat (unused but kept)
    const handleRunQuery = async () => {
        setLoading(true);
        setQueryData([]);
        try {
        } catch (error) {
            if (error instanceof SyntaxError) {
                toast.error("El JSON de la consulta es inválido.");
            } else {
                toast.error(error.message || "Error al ejecutar la consulta");
            }
        } finally {
            setLoading(false);
        }
    };

    const renderTable = (data) => {
        if (!data || data.length === 0) {
            return (
                <div className={styles.emptyState}>
                    <FaDatabase size={40} style={{ opacity: 0.3, marginBottom: '1rem' }} />
                    <p>No hay datos para mostrar.</p>
                </div>
            );
        }

        const columns = Object.keys(data[0]);

        return (
            <div className={styles.tableWrapper}>
                <table className={styles.dynamicTable}>
                    <thead>
                        <tr>
                            {columns.map(col => <th key={col}>{col}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row, idx) => (
                            <tr key={idx}>
                                {columns.map(col => {
                                    let cellData = row[col];
                                    if (typeof cellData === 'object' && cellData !== null) {
                                        cellData = JSON.stringify(cellData);
                                    }
                                    return <td key={col}>{String(cellData ?? '')}</td>;
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const handleMainTabChange = (tab) => {
        if (tab === 'queries' && !builder.selectedTable) setActiveBuilderStage('data');
        setActiveTab(tab);
    };

    return (
        <DashboardLayout>
            <div className={`page-container ${styles.datamartPageContainer}`}>
                <div className={styles.mobileTabSelector}>
                    <select
                        value={activeTab}
                        onChange={(event) => handleMainTabChange(event.currentTarget.value)}
                        aria-label="Seleccionar sección de Datamart"
                    >
                        <option value="dictionary">Diccionario de bases de datos</option>
                        <option value="redis">Diccionario de datos Redis</option>
                        <option value="ai_control">Control de Acceso IA</option>
                        <option value="views">Vistas preestablecidas</option>
                        <option value="queries">Consultas dinámicas</option>
                    </select>
                    <FaChevronDown aria-hidden="true" />
                </div>

                <div className={`${styles.headerTitleRow} ${activeTab === 'ai_control' ? styles.aiControlHeader : ''}`}>
                    <div>
                        <p className={`page-subtitle ${activeTab === 'ai_control' ? styles.aiControlDesktopSubtitle : ''}`}>
                            Abstracción de datos, catálogo de bases de datos y gobierno de acceso de solo lectura para la IA.
                        </p>
                        {activeTab === 'ai_control' && (
                            <p className={styles.aiControlMobileSubtitle}>
                                Reglas y accesos disponibles para la IA
                            </p>
                        )}
                    </div>
                    {activeTab === 'dictionary' && !isResponsiveDictionary && (
                        <div className={styles.headerActions}>
                            <button
                                className={styles.secondaryActionBtn}
                                onClick={fetchDictionaries}
                                disabled={loadingDict}
                                title="Recargar catálogo de bases de datos"
                            >
                                <FaSync className={loadingDict ? styles.spinIcon : ''} />
                                Actualizar
                            </button>
                            <button
                                className={styles.secondaryActionBtn}
                                onClick={handleExportJson}
                                disabled={dictionaries.length === 0}
                                title="Exportar diccionario en formato JSON"
                            >
                                <FaDownload /> Exportar JSON
                            </button>
                        </div>
                    )}
                    {activeTab === 'ai_control' && (
                        <div className={`${styles.headerActions} ${styles.aiControlHeaderActions}`}>
                            <button
                                className={styles.secondaryActionBtn}
                                onClick={fetchAuditLogs}
                                disabled={loadingLogs}
                                aria-label="Ver bitácora de auditoría de consultas de IA"
                                title="Ver bitácora de auditoría de consultas de IA"
                            >
                                <FaHistory />
                                <span className={styles.aiControlDesktopActionLabel}>Bitácora Auditoría</span>
                                <span className={styles.aiControlMobileActionLabel}>Auditoría</span>
                            </button>
                            <button
                                className={styles.actionBtnPrimary}
                                onClick={handleStartCreateAiInBuilder}
                                aria-label="Crear regla con el Constructor Dinámico"
                                title="Crear consulta con el Constructor Dinámico para el Asistente IA"
                            >
                                <FaTerminal aria-hidden="true" />
                                <span className={styles.aiControlDesktopActionLabel}>Crear Regla con Constructor</span>
                                <span className={styles.aiControlMobileActionLabel}>Crear regla</span>
                            </button>
                        </div>
                    )}
                    {activeTab === 'redis' && (
                        <div className={styles.headerActions}>
                            <button
                                className={styles.secondaryActionBtn}
                                onClick={() => {
                                    fetchRedisDictionary();
                                    fetchRedisLiveKeys();
                                    toast.success("Métricas y claves de Redis actualizadas");
                                }}
                                disabled={loadingRedis || loadingLiveKeys}
                                title="Actualizar estado e inspección en vivo de Redis"
                            >
                                <FaSync className={(loadingRedis || loadingLiveKeys) ? styles.spinIcon : ''} />
                                Actualizar Redis
                            </button>
                            <button
                                className={styles.secondaryActionBtn}
                                onClick={handleExportRedisJson}
                                disabled={!redisData}
                                title="Exportar especificación del diccionario Redis en JSON"
                            >
                                <FaDownload /> Exportar JSON
                            </button>
                        </div>
                    )}
                </div>

                {/* Data dictionary summary metrics. */}
                {activeTab === 'dictionary' && !isResponsiveDictionary && (
                    <div className={styles.kpiGrid}>
                        <div className={styles.kpiCard}>
                            <div className={styles.kpiIconWrapper} style={{ background: '#EEF2FF', color: 'var(--color-primary)' }}>
                                <FaServer size={22} />
                            </div>
                            <div className={styles.kpiInfo}>
                                <span className={styles.kpiLabel}>Bases de Datos</span>
                                <span className={styles.kpiValue}>{globalMetrics.totalDbs}</span>
                                <span className={styles.kpiSub}>PostgreSQL 15 Activas</span>
                            </div>
                        </div>

                        <div className={styles.kpiCard}>
                            <div className={styles.kpiIconWrapper} style={{ background: '#ECFDF5', color: '#059669' }}>
                                <FaTable size={22} />
                            </div>
                            <div className={styles.kpiInfo}>
                                <span className={styles.kpiLabel}>Tablas de Dominio</span>
                                <span className={styles.kpiValue}>{globalMetrics.totalTables}</span>
                                <span className={styles.kpiSub}>Estructuradas en el catálogo</span>
                            </div>
                        </div>

                        <div className={styles.kpiCard}>
                            <div className={styles.kpiIconWrapper} style={{ background: '#EFF6FF', color: '#2563EB' }}>
                                <FaColumns size={22} />
                            </div>
                            <div className={styles.kpiInfo}>
                                <span className={styles.kpiLabel}>Columnas Documentadas</span>
                                <span className={styles.kpiValue}>{globalMetrics.totalCols}</span>
                                <span className={styles.kpiSub}>Tipos, restricciones y llaves</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Redis data dictionary summary metrics. */}
                {activeTab === 'redis' && !isResponsiveDictionary && (
                    <div className={styles.kpiGrid}>
                        <div className={styles.kpiCard}>
                            <div className={styles.kpiIconWrapper} style={{ background: '#FEF2F2', color: '#DC2626' }}>
                                <SiRedis size={24} />
                            </div>
                            <div className={styles.kpiInfo}>
                                <span className={styles.kpiLabel}>Namespaces Activos</span>
                                <span className={styles.kpiValue}>{redisData?.total_namespaces || 3}</span>
                                <span className={styles.kpiSub}>Estructuras Documentadas</span>
                            </div>
                        </div>

                        <div className={styles.kpiCard}>
                            <div className={styles.kpiIconWrapper} style={{ background: redisData?.server_status?.connected ? '#ECFDF5' : '#FEF2F2', color: redisData?.server_status?.connected ? '#059669' : '#DC2626' }}>
                                <FaServer size={22} />
                            </div>
                            <div className={styles.kpiInfo}>
                                <span className={styles.kpiLabel}>Estado Motor Redis</span>
                                <span className={styles.kpiValue} style={{ fontSize: '1.25rem', display: 'flex', alignItems: 'center' }}>
                                    <span className={`${styles.redisStatusDot} ${redisData?.server_status?.connected ? styles.redisStatusOnline : styles.redisStatusOffline}`} />
                                    {redisData?.server_status?.connected ? 'EN LÍNEA' : 'OFFLINE'}
                                </span>
                                <span className={styles.kpiSub}>
                                    {redisData?.server_status?.connected
                                        ? `Redis v${redisData?.server_status?.redis_version || '7.x'} (${redisData?.server_status?.uptime_in_days || 0}d uptime)`
                                        : 'Sin conexión al contenedor'}
                                </span>
                            </div>
                        </div>

                        <div className={styles.kpiCard}>
                            <div className={styles.kpiIconWrapper} style={{ background: '#EEF2FF', color: '#4F46E5' }}>
                                <FaMemory size={22} />
                            </div>
                            <div className={styles.kpiInfo}>
                                <span className={styles.kpiLabel}>Memoria & Política</span>
                                <span className={styles.kpiValue} style={{ fontSize: '1.25rem' }}>
                                    {redisData?.server_status?.used_memory_human || 'N/A'} <span style={{ fontSize: '0.85rem', color: '#6B7280', fontWeight: 400 }}>/ {redisData?.server_status?.maxmemory_human || '128M'}</span>
                                </span>
                                <span className={styles.kpiSub}>Política: {redisData?.server_status?.maxmemory_policy || 'allkeys-lru'}</span>
                            </div>
                        </div>

                        <div className={styles.kpiCard}>
                            <div className={styles.kpiIconWrapper} style={{ background: '#FFFBEB', color: '#D97706' }}>
                                <FaBolt size={22} />
                            </div>
                            <div className={styles.kpiInfo}>
                                <span className={styles.kpiLabel}>Claves en Vivo</span>
                                <span className={styles.kpiValue}>{redisLiveKeys.length}</span>
                                <span className={styles.kpiSub}>Inspeccionadas vía SCAN</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* AI access control summary metrics. */}
                {activeTab === 'ai_control' && (
                    <div className={`${styles.kpiGrid} ${styles.aiControlKpiGrid}`}>
                        <div className={styles.kpiCard}>
                            <div className={`${styles.kpiIconWrapper} ${styles.aiControlKpiIcon}`} style={{ background: '#EEF2FF', color: 'var(--color-primary)' }}>
                                <FaRobot size={22} />
                            </div>
                            <div className={styles.kpiInfo}>
                                <span className={styles.kpiLabel}>Consultas Registradas</span>
                                <span className={styles.kpiValue}>{aiRules.length}</span>
                                <span className={styles.kpiSub}>Almacenadas en datamart_db</span>
                            </div>
                        </div>

                        <div className={styles.kpiCard}>
                            <div className={`${styles.kpiIconWrapper} ${styles.aiControlKpiIcon}`} style={{ background: '#ECFDF5', color: '#059669' }}>
                                <FaCheckCircle size={22} />
                            </div>
                            <div className={styles.kpiInfo}>
                                <span className={styles.kpiLabel}>Accesos Habilitados</span>
                                <span className={styles.kpiValue}>{aiRules.filter(r => r.is_enabled).length}</span>
                                <span className={styles.kpiSub}>Disponibles para el RAG</span>
                            </div>
                        </div>

                        <div className={styles.kpiCard}>
                            <div className={`${styles.kpiIconWrapper} ${styles.aiControlKpiIcon}`} style={{ background: '#FEF2F2', color: '#DC2626' }}>
                                <FaShieldAlt size={22} />
                            </div>
                            <div className={styles.kpiInfo}>
                                <span className={styles.kpiLabel}>Modo de Operación</span>
                                <span className={styles.kpiValue}>READ ONLY</span>
                                <span className={styles.kpiSub}>Bloqueo estricto DML/DDL</span>
                            </div>
                        </div>
                    </div>
                )}

                <div className={styles.datamartContainer}>
                    {/* Tab bar. */}
                    <div className={styles.tabsBar}>
                        <button
                            className={`${styles.tabBtn} ${activeTab === 'dictionary' ? styles.tabBtnActive : ''}`}
                            onClick={() => handleMainTabChange('dictionary')}
                        >
                            <FaDatabase style={{ marginRight: '6px' }} /> Diccionario de Bases de Datos
                        </button>
                        <button
                            className={`${styles.tabBtn} ${activeTab === 'redis' ? styles.tabBtnActive : ''}`}
                            onClick={() => handleMainTabChange('redis')}
                        >
                            <SiRedis style={{ marginRight: '6px', color: '#DC2626' }} /> Diccionario de Datos Redis (Caché & Rate Limits)
                        </button>
                        <button
                            className={`${styles.tabBtn} ${activeTab === 'ai_control' ? styles.tabBtnActive : ''}`}
                            onClick={() => handleMainTabChange('ai_control')}
                        >
                            <FaRobot style={{ marginRight: '6px' }} /> Control de Acceso IA (datamart_db)
                        </button>
                        <button
                            className={`${styles.tabBtn} ${activeTab === 'views' ? styles.tabBtnActive : ''}`}
                            onClick={() => handleMainTabChange('views')}
                        >
                            <FaLayerGroup style={{ marginRight: '6px' }} /> Vistas Preestablecidas
                        </button>
                        <button
                            className={`${styles.tabBtn} ${activeTab === 'queries' ? styles.tabBtnActive : ''}`}
                            onClick={() => handleMainTabChange('queries')}
                        >
                            <FaTerminal style={{ marginRight: '6px' }} /> Consultas Dinámicas
                        </button>
                    </div>

                    <div className={styles.contentArea}>

                        {/* TAB 2: REDIS DATA DICTIONARY. */}
                        {activeTab === 'redis' && (
                            <div className={styles.redisContainer}>
                                {/* Redis offline state. */}
                                {redisData && !redisData.server_status?.connected && (
                                    <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '10px', padding: '1rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                        <FaExclamationTriangle style={{ color: '#DC2626', flexShrink: 0 }} />
                                        <div>
                                            <p style={{ fontWeight: 600, color: '#B91C1C', margin: 0 }}>Servidor Redis no disponible</p>
                                            <p style={{ fontSize: '0.83rem', color: '#7F1D1D', margin: '0.2rem 0 0' }}>
                                                La inspección en vivo de claves está deshabilitada. El diccionario formal está disponible. Error: {redisData.server_status?.error}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {isResponsiveDictionary && (
                                    <div className={styles.redisResponsiveStatus} role="status">
                                        <span className={`${styles.redisResponsiveStatusDot} ${redisData?.server_status?.connected ? styles.redisStatusOnline : styles.redisStatusOffline}`} />
                                        <span>
                                            {loadingRedis && !redisData
                                                ? 'Conectando con Redis'
                                                : redisData?.server_status?.connected
                                                    ? 'Redis en línea'
                                                    : 'Redis sin conexión'}
                                        </span>
                                    </div>
                                )}

                                {isResponsiveDictionary && (
                                    <div className={styles.redisSubtabs} role="tablist" aria-label="Secciones del diccionario Redis">
                                        {REDIS_RESPONSIVE_SECTIONS.map((section) => {
                                            const SectionIcon = section.icon;
                                            const isActive = activeRedisSection === section.id;
                                            return (
                                                <button
                                                    key={section.id}
                                                    id={`redis-responsive-tab-${section.id}`}
                                                    type="button"
                                                    role="tab"
                                                    aria-selected={isActive}
                                                    aria-controls="redis-responsive-panel"
                                                    tabIndex={isActive ? 0 : -1}
                                                    className={`${styles.redisSubtab} ${isActive ? styles.redisSubtabActive : ''}`}
                                                    onClick={() => setActiveRedisSection(section.id)}
                                                    onKeyDown={(event) => handleRedisSubtabKeyDown(event, section.id)}
                                                >
                                                    <SectionIcon aria-hidden="true" />
                                                    <span>{section.label}</span>
                                                    {section.id === 'live_keys' && (
                                                        <span className={styles.redisSubtabCount}>{redisLiveKeys.length}</span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}

                                <div
                                    className={styles.redisResponsivePanel}
                                    id="redis-responsive-panel"
                                    role={isResponsiveDictionary ? 'tabpanel' : undefined}
                                    aria-labelledby={isResponsiveDictionary ? `redis-responsive-tab-${activeRedisSection}` : undefined}
                                >
                                    {(!isResponsiveDictionary || activeRedisSection === 'dictionary') && (
                                        <>
                                {/* Filter toolbar. */}
                                <div className={styles.redisToolbar}>
                                    <div className={styles.redisFilterGroup}>
                                        <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#4B5563' }}>Microservicio:</span>
                                        {['ALL', 'ms-identity-access', 'ms-api-gateway'].map(svc => (
                                            <button
                                                key={svc}
                                                className={`${styles.redisFilterBtn} ${redisServiceFilter === svc ? styles.redisFilterBtnActive : ''}`}
                                                onClick={() => setRedisServiceFilter(svc)}
                                            >
                                                {svc === 'ALL' ? '🔍 Todos' : svc}
                                            </button>
                                        ))}
                                    </div>
                                    <div className={styles.redisSearchBox}>
                                        <FaSearch className={styles.redisSearchIcon} />
                                        <input
                                            type="text"
                                            className={styles.redisSearchInput}
                                            placeholder="Buscar namespace, categoría, descripción..."
                                            value={redisSearch}
                                            onChange={e => setRedisSearch(e.target.value)}
                                        />
                                    </div>
                                </div>

                                {/* Dictionary namespaces. */}
                                {loadingRedis ? (
                                    <div className={styles.loadingContainer}>
                                        <FaSpinner className={styles.spinIcon} size={28} />
                                        <p>Cargando diccionario de Redis...</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className={styles.redisSectionHeader}>
                                            <h3 className={styles.redisSectionTitle}>
                                                <SiRedis style={{ color: '#DC2626' }} />
                                                Namespaces del Diccionario
                                                <span className={styles.redisCountBadge}>{filteredRedisNamespaces.length}</span>
                                            </h3>
                                        </div>
                                        <div className={styles.redisCardsGrid}>
                                            {filteredRedisNamespaces.map(ns => {
                                                const defaultExpanded = !isResponsiveDictionary
                                                    && ['pwd_reset', 'rate_limiter_ip'].includes(ns.id);
                                                const isExpanded = expandedNamespaces[ns.id] ?? defaultExpanded;
                                                const CATEGORY_COLORS = {
                                                    'AUTENTICACIÓN Y SEGURIDAD': { bg: '#FEF2F2', color: '#DC2626', icon: FaShieldAlt },
                                                    'GOBERNANZA DE RED Y RATE LIMITING': { bg: '#EEF2FF', color: '#4F46E5', icon: FaNetworkWired }
                                                };
                                                const style = CATEGORY_COLORS[ns.category] || { bg: '#F3F4F6', color: '#4B5563', icon: FaKey };
                                                const IconComp = style.icon;
                                                return (
                                                    <div className={styles.redisCard} key={ns.id}>
                                                        <div className={styles.redisCardHeader}>
                                                            <div className={styles.redisCardIconWrapper} style={{ background: style.bg, color: style.color }}>
                                                                <IconComp size={20} />
                                                            </div>
                                                            <div className={styles.redisCardTitleCol}>
                                                                <div className={styles.redisCardTitleRow}>
                                                                    <span className={styles.redisCardTitle}>{ns.name}</span>
                                                                </div>
                                                                <code className={styles.redisNamespaceCode}>{ns.namespace}</code>
                                                                <div className={styles.redisBadgeRow}>
                                                                    <span className={styles.redisBadgeType}>{ns.data_type}</span>
                                                                    <span className={styles.redisBadgeTtl}>
                                                                        <FaClock size={10} /> TTL: {ns.ttl_display}
                                                                    </span>
                                                                    <span className={styles.redisBadgeService}>{ns.owner_service}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className={styles.redisCardBody}>
                                                            <p className={styles.redisCardDesc}>{ns.description}</p>

                                                            {/* Expandable details. */}
                                                            <button
                                                                className={styles.redisExpandBtn}
                                                                onClick={() => handleToggleNamespaceExpand(ns.id, isExpanded)}
                                                            >
                                                                {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                                                                {isExpanded ? 'Ocultar detalles' : 'Ver especificación completa'}
                                                            </button>

                                                            {isExpanded && (
                                                                <div className={styles.redisDetailsDrawer}>
                                                                    {/* Component / owner. */}
                                                                    <div className={styles.redisDetailBlock}>
                                                                        <div className={styles.redisDetailLabel}><FaCode /> Componente Propietario</div>
                                                                        <code className={styles.redisDetailValue}>{ns.owner_component}</code>
                                                                    </div>

                                                                    {/* Eviction policy. */}
                                                                    <div className={styles.redisDetailBlock}>
                                                                        <div className={styles.redisDetailLabel}><FaShieldAlt /> Política de Desalojo & Seguridad</div>
                                                                        <p style={{ fontSize: '0.83rem', color: '#374151', margin: 0 }}>{ns.eviction_policy}</p>
                                                                        {ns.security_notes && (
                                                                            <p style={{ fontSize: '0.8rem', color: '#6B7280', marginTop: '0.35rem', fontStyle: 'italic' }}>
                                                                                🔒 {ns.security_notes}
                                                                            </p>
                                                                        )}
                                                                    </div>

                                                                    {/* Value structure. */}
                                                                    {ns.value_structure && (
                                                                        <div className={styles.redisDetailBlock}>
                                                                            <div className={styles.redisDetailLabel}><FaCode /> Estructura del Valor</div>
                                                                            <p style={{ fontSize: '0.82rem', color: '#374151', marginBottom: '0.4rem' }}>
                                                                                Formato: <strong>{ns.value_structure.format}</strong>
                                                                            </p>
                                                                            {ns.value_structure.example && (
                                                                                <div className={styles.redisCodeBox} style={{ fontSize: '0.72rem', wordBreak: 'break-all' }}>
                                                                                    {ns.value_structure.example}
                                                                                </div>
                                                                            )}
                                                                            {ns.value_structure.claims && (
                                                                                <table className={styles.redisSubTable} style={{ marginTop: '0.5rem' }}>
                                                                                    <thead>
                                                                                        <tr>
                                                                                            <th>Claim</th>
                                                                                            <th>Descripción</th>
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody>
                                                                                        {Object.entries(ns.value_structure.claims).map(([claim, desc]) => (
                                                                                            <tr key={claim}>
                                                                                                <td><code>{claim}</code></td>
                                                                                                <td>{desc}</td>
                                                                                            </tr>
                                                                                        ))}
                                                                                    </tbody>
                                                                                </table>
                                                                            )}
                                                                        </div>
                                                                    )}

                                                                    {/* Redis commands. */}
                                                                    {ns.operations && ns.operations.length > 0 && (
                                                                        <div className={styles.redisDetailBlock}>
                                                                            <div className={styles.redisDetailLabel}><FaTerminal /> Comandos Redis</div>
                                                                            {ns.operations.map((op, idx) => (
                                                                                <div key={idx} style={{ marginBottom: '0.75rem' }}>
                                                                                    <p style={{ fontSize: '0.78rem', fontWeight: 600, color: '#4F46E5', marginBottom: '0.2rem' }}>
                                                                                        {op.operation}
                                                                                    </p>
                                                                                    <div className={styles.redisCodeBox}>
                                                                                        <span style={{ color: '#7DD3FC' }}>{op.command.split(' ')[0]}</span>{' '}
                                                                                        <span style={{ color: '#A5F3FC' }}>{op.command.split(' ').slice(1).join(' ')}</span>
                                                                                        <button
                                                                                            className={styles.redisCopyBtn}
                                                                                            onClick={() => handleCopyCommand(op.command)}
                                                                                            title="Copiar comando"
                                                                                        >
                                                                                            <FaCopy /> Copiar
                                                                                        </button>
                                                                                    </div>
                                                                                    <p style={{ fontSize: '0.77rem', color: '#6B7280', marginTop: '0.25rem' }}>{op.description}</p>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}

                                                                    {/* Rate-limiting route configuration. */}
                                                                    {ns.rules_config && ns.rules_config.length > 0 && (
                                                                        <div className={styles.redisDetailBlock}>
                                                                            <div className={styles.redisDetailLabel}><FaNetworkWired /> Configuración de Rutas (Rate Limits)</div>
                                                                            {isResponsiveDictionary ? (
                                                                                <div className={styles.redisRouteCards}>
                                                                                    {ns.rules_config.map((rc, idx) => (
                                                                                        <article className={styles.redisRouteCard} key={`${rc.route}-${idx}`}>
                                                                                            <div className={styles.redisRouteCardHeader}>
                                                                                                <code>{rc.route}</code>
                                                                                                <span>{rc.replenish_rate} rep/s</span>
                                                                                            </div>
                                                                                            <div className={styles.redisRouteCardBody}>
                                                                                                <span><strong>Burst:</strong> {rc.burst_capacity}</span>
                                                                                                <p>{rc.purpose}</p>
                                                                                            </div>
                                                                                        </article>
                                                                                    ))}
                                                                                </div>
                                                                            ) : (
                                                                                <table className={styles.redisSubTable}>
                                                                                    <thead>
                                                                                        <tr>
                                                                                            <th>Ruta</th>
                                                                                            <th>Rep/s</th>
                                                                                            <th>Burst</th>
                                                                                            <th>Propósito</th>
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody>
                                                                                        {ns.rules_config.map((rc, idx) => (
                                                                                            <tr key={idx}>
                                                                                                <td><code>{rc.route}</code></td>
                                                                                                <td style={{ fontWeight: 700, color: '#4F46E5' }}>{rc.replenish_rate}</td>
                                                                                                <td style={{ fontWeight: 700, color: '#D97706' }}>{rc.burst_capacity}</td>
                                                                                                <td style={{ fontSize: '0.75rem' }}>{rc.purpose}</td>
                                                                                            </tr>
                                                                                        ))}
                                                                                    </tbody>
                                                                                </table>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                                        </>
                                    )}

                                {/* Live key inspector. */}
                                {(!isResponsiveDictionary || activeRedisSection === 'live_keys') && (
                                <div className={styles.redisLiveCard}>
                                    <div className={styles.redisSectionHeader}>
                                        <h3 className={styles.redisSectionTitle}>
                                            <FaBolt style={{ color: '#D97706' }} />
                                            Inspector de Claves en Vivo
                                            <span className={styles.redisCountBadge} style={{ background: '#FEF3C7', color: '#92400E' }}>{redisLiveKeys.length}</span>
                                        </h3>
                                    </div>
                                    <div className={styles.redisLiveFilterRow}>
                                        <div className={styles.redisQuickPills}>
                                            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#4B5563', marginRight: '0.25rem' }}>Filtros rápidos:</span>
                                            {['*', 'pwd_reset:*', 'request_rate_limiter.*'].map(ptn => (
                                                <button
                                                    key={ptn}
                                                    type="button"
                                                    className={`${styles.redisQuickPill} ${liveKeyPattern === ptn ? styles.redisQuickPillActive : ''}`}
                                                    onClick={() => {
                                                        setLiveKeyPattern(ptn);
                                                        fetchRedisLiveKeys(ptn);
                                                    }}
                                                >
                                                    {ptn}
                                                </button>
                                            ))}
                                        </div>
                                        <div className={styles.redisCustomPatternGroup}>
                                            <div className={styles.redisSearchBox}>
                                                <FaSearch className={styles.redisSearchIcon} />
                                                <input
                                                    type="text"
                                                    className={styles.redisSearchInput}
                                                    placeholder="Patrón personalizado..."
                                                    value={liveKeyPattern}
                                                    onChange={e => setLiveKeyPattern(e.target.value)}
                                                    onKeyDown={e => e.key === 'Enter' && fetchRedisLiveKeys(liveKeyPattern)}
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                className={styles.secondaryActionBtn}
                                                onClick={() => fetchRedisLiveKeys(liveKeyPattern)}
                                                disabled={loadingLiveKeys}
                                            >
                                                <FaSync className={loadingLiveKeys ? styles.spinIcon : ''} />
                                                Inspeccionar
                                            </button>
                                        </div>
                                    </div>

                                    {loadingLiveKeys ? (
                                        <div className={styles.loadingContainer}>
                                            <FaSpinner className={styles.spinIcon} size={24} />
                                            <p>Consultando claves en Redis...</p>
                                        </div>
                                    ) : redisLiveKeys.length === 0 ? (
                                        <div className={styles.emptyState}>
                                            <FaBolt size={36} style={{ color: '#D1D5DB', marginBottom: '0.75rem' }} />
                                            <p style={{ fontWeight: 600, color: '#6B7280' }}>No hay claves activas que coincidan con el patrón <code style={{ background: '#F3F4F6', padding: '0.15rem 0.4rem', borderRadius: '4px' }}>{liveKeyPattern}</code></p>
                                            <p style={{ fontSize: '0.82rem', color: '#9CA3AF' }}>Los buckets de rate limiting y tokens de recuperación se crean dinámicamente.</p>
                                        </div>
                                    ) : isResponsiveDictionary ? (
                                        <div className={styles.redisLiveCards}>
                                            {redisLiveKeys.map((liveKey) => {
                                                const ttlPillClass = liveKey.ttl > 60 ? styles.redisTtlPillGreen
                                                    : liveKey.ttl > 0 ? styles.redisTtlPillYellow
                                                    : styles.redisTtlPillRed;
                                                return (
                                                    <article className={styles.redisLiveKeyCard} key={liveKey.key}>
                                                        <div className={styles.redisLiveKeyCardHeader}>
                                                            <code className={styles.redisKeyCell}>{liveKey.key}</code>
                                                            <span className={ttlPillClass}>
                                                                <FaClock aria-hidden="true" /> {liveKey.ttl_human}
                                                            </span>
                                                        </div>
                                                        <dl className={styles.redisLiveKeyMetadata}>
                                                            <div>
                                                                <dt>Namespace</dt>
                                                                <dd><code>{liveKey.matched_namespace}</code></dd>
                                                            </div>
                                                            <div>
                                                                <dt>Tipo</dt>
                                                                <dd><span className={styles.redisBadgeType}>{liveKey.type}</span></dd>
                                                            </div>
                                                            <div>
                                                                <dt>Categoría</dt>
                                                                <dd>{liveKey.category}</dd>
                                                            </div>
                                                        </dl>
                                                    </article>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div style={{ overflowX: 'auto' }}>
                                            <table className={styles.redisLiveTable}>
                                                <thead>
                                                    <tr>
                                                        <th>Clave</th>
                                                        <th>Namespace</th>
                                                        <th>Tipo</th>
                                                        <th>TTL Restante</th>
                                                        <th>Categoría</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {redisLiveKeys.map((lk, idx) => {
                                                        const ttlPillClass = lk.ttl > 60 ? styles.redisTtlPillGreen
                                                            : lk.ttl > 0 ? styles.redisTtlPillYellow
                                                            : styles.redisTtlPillRed;
                                                        return (
                                                            <tr key={idx}>
                                                                <td>
                                                                    <span className={styles.redisKeyCell}>{lk.key}</span>
                                                                </td>
                                                                <td style={{ fontSize: '0.8rem', color: '#475569' }}>
                                                                    <code style={{ background: '#FEF2F2', color: '#B91C1C', padding: '0.1rem 0.35rem', borderRadius: '4px', fontSize: '0.75rem' }}>
                                                                        {lk.matched_namespace}
                                                                    </code>
                                                                </td>
                                                                <td>
                                                                    <span className={styles.redisBadgeType}>{lk.type}</span>
                                                                </td>
                                                                <td>
                                                                    <span className={ttlPillClass}>
                                                                        <FaClock size={10} />{lk.ttl_human}
                                                                    </span>
                                                                </td>
                                                                <td style={{ fontSize: '0.78rem', color: '#4B5563' }}>{lk.category}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                                )}

                                {/* Redis data governance. */}
                                {redisData?.governance && (!isResponsiveDictionary || activeRedisSection === 'governance') && (
                                    <div>
                                        <div className={styles.redisSectionHeader}>
                                            <h3 className={styles.redisSectionTitle}>
                                                <FaShieldAlt style={{ color: '#4F46E5' }} />
                                                Gobernanza y Políticas de Seguridad
                                            </h3>
                                        </div>
                                        <div className={styles.redisGovGrid}>
                                            <details className={styles.redisGovAccordion} open={!isResponsiveDictionary}>
                                                <summary className={styles.redisGovAccordionSummary}>
                                                    <span className={styles.redisGovTitle}>
                                                        <FaCode aria-hidden="true" style={{ color: '#059669' }} /> Persistencia de Datos
                                                    </span>
                                                </summary>
                                                <div className={styles.redisGovAccordionBody}>
                                                    <div className={styles.redisGovItem}>🗂 Motor: <strong>{redisData.governance.persistence.engine}</strong></div>
                                                    <div className={styles.redisGovItem}>⚡ Política: <code>{redisData.governance.persistence.policy}</code></div>
                                                    <div className={styles.redisGovItem}>📁 Auto-rewrite: {redisData.governance.persistence.auto_aof_rewrite_percentage} / {redisData.governance.persistence.auto_aof_rewrite_min_size}</div>
                                                    <div className={styles.redisGovItem} style={{ fontStyle: 'italic', color: '#6B7280', marginTop: '0.25rem' }}>
                                                        {redisData.governance.persistence.description}
                                                    </div>
                                                </div>
                                            </details>
                                            <details className={styles.redisGovAccordion} open={!isResponsiveDictionary}>
                                                <summary className={styles.redisGovAccordionSummary}>
                                                    <span className={styles.redisGovTitle}>
                                                        <FaMemory aria-hidden="true" style={{ color: '#D97706' }} /> Gestión de Memoria
                                                    </span>
                                                </summary>
                                                <div className={styles.redisGovAccordionBody}>
                                                    <div className={styles.redisGovItem}>💾 Límite máximo: <strong>{redisData.governance.memory_management.max_memory}</strong></div>
                                                    <div className={styles.redisGovItem}>🔄 Política de desalojo: <code>{redisData.governance.memory_management.eviction_policy}</code></div>
                                                    <div className={styles.redisGovItem} style={{ fontStyle: 'italic', color: '#6B7280', marginTop: '0.25rem' }}>
                                                        {redisData.governance.memory_management.description}
                                                    </div>
                                                </div>
                                            </details>
                                            <details className={`${styles.redisGovAccordion} ${styles.redisGovAccordionWide}`} open={!isResponsiveDictionary}>
                                                <summary className={styles.redisGovAccordionSummary}>
                                                    <span className={styles.redisGovTitle}>
                                                        <FaShieldAlt aria-hidden="true" style={{ color: '#4F46E5' }} /> Directrices de Seguridad
                                                    </span>
                                                </summary>
                                                <div className={styles.redisGovAccordionBody}>
                                                    {redisData.governance.security_guidelines.map((guideline, idx) => (
                                                        <div key={idx} className={styles.redisGovItem}>
                                                            <span style={{ flexShrink: 0 }}>🔐</span>
                                                            {guideline}
                                                        </div>
                                                    ))}
                                                </div>
                                            </details>
                                        </div>
                                    </div>
                                )}
                                    </div>
                            </div>
                        )}

                        {/* TAB 1: DATABASE DICTIONARY. */}
                        {activeTab === 'dictionary' && (
                            <div className={styles.dictionaryRoot}>
                                {!isResponsiveDictionary && (
                                    <div className={styles.filterBar}>
                                        <div className={styles.searchBox}>
                                            <FaSearch className={styles.searchIcon} />
                                            <input
                                                type="text"
                                                className={styles.searchInput}
                                                placeholder="Buscar base de datos, tabla o columna (ej. users, role, email)..."
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                            />
                                            {searchQuery && (
                                                <button
                                                    className={styles.clearSearchBtn}
                                                    onClick={() => setSearchQuery('')}
                                                >
                                                    <FaTimes />
                                                </button>
                                            )}
                                        </div>
                                        <label className={styles.checkboxLabel}>
                                            <input
                                                type="checkbox"
                                                checked={hideSystemTables}
                                                onChange={(e) => setHideSystemTables(e.target.checked)}
                                            />
                                            <span>Ocultar tablas de sistema (Flyway)</span>
                                        </label>
                                    </div>
                                )}

                                {loadingDict ? (
                                    <div className={styles.loadingContainer}>
                                        <FaSpinner className={styles.spinIcon} size={32} />
                                        <p>Analizando catálogo de PostgreSQL e introspeccionando esquemas...</p>
                                    </div>
                                ) : isResponsiveDictionary ? (
                                    <ResponsiveDictionary
                                        databases={responsiveDatabases}
                                        currentDb={currentDb}
                                        currentTables={currentTables}
                                        currentTable={currentTable}
                                        selectedDbName={selectedDbName}
                                        searchQuery={searchQuery}
                                        hideSystemTables={hideSystemTables}
                                        loading={loadingDict}
                                        hasDatabases={dictionaries.length > 0}
                                        onSelectDb={handleSelectDb}
                                        onSelectTable={setSelectedTableName}
                                        onSearchChange={setSearchQuery}
                                        onHideSystemTablesChange={setHideSystemTables}
                                        onRefresh={fetchDictionaries}
                                        onExport={handleExportJson}
                                        onOpenSample={handleOpenSample}
                                        onSendToQuery={handleSendToQuery}
                                    />
                                ) : (
                                    <>
                                        {/* Database selector. */}
                                        <div className={styles.dbSelectorContainer}>
                                            <div className={styles.dbPillsTrack}>
                                                {filteredDatabases.map(db => {
                                                    const isSelected = db.name === selectedDbName;
                                                    const validTables = (db.tables || []).filter(t => !hideSystemTables || !t.is_system);
                                                    return (
                                                        <button
                                                            key={db.name}
                                                            className={`${styles.dbPill} ${isSelected ? styles.dbPillActive : ''}`}
                                                            onClick={() => handleSelectDb(db.name)}
                                                        >
                                                            <div className={styles.dbPillHeader}>
                                                                <FaDatabase className={styles.dbPillIcon} />
                                                                <span className={styles.dbPillName}>{db.name}</span>
                                                            </div>
                                                            <div className={styles.dbPillMeta}>
                                                                <span className={styles.dbCategoryBadge}>{db.category || 'General'}</span>
                                                                <span className={styles.dbTableCount}>{validTables.length} tablas</span>
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Main layout: table list (left) and table details (right). */}
                                        {currentDb ? (
                                            <div className={styles.schemaLayout}>
                                                <div className={styles.tablesSidebar}>
                                                    <div className={styles.sidebarHeader}>
                                                        <div className={styles.sidebarTitleGroup}>
                                                            <h4 className={styles.sidebarTitle}>{currentDb.title || currentDb.name}</h4>
                                                            <p className={styles.sidebarDesc}>{currentDb.description}</p>
                                                        </div>
                                                        <span className={styles.badgeNeutral}>
                                                            {currentTables.length} {currentTables.length === 1 ? 'tabla' : 'tablas'}
                                                        </span>
                                                    </div>

                                                    <div className={styles.tableListScroll}>
                                                        {currentTables.length === 0 ? (
                                                            <div className={styles.noTablesNotice}>
                                                                <FaInfoCircle />
                                                                <span>No hay tablas creadas en esta base de datos.</span>
                                                            </div>
                                                        ) : (
                                                            currentTables.map(tbl => {
                                                                const isTableActive = currentTable && currentTable.name === tbl.name;
                                                                const matchesQuery = searchQuery && (
                                                                    tbl.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                                                    (tbl.columns || []).some(c => c.name.toLowerCase().includes(searchQuery.toLowerCase()))
                                                                );

                                                                return (
                                                                    <div
                                                                        key={tbl.name}
                                                                        className={`${styles.tableListItem} ${isTableActive ? styles.tableListItemActive : ''} ${matchesQuery ? styles.highlightMatch : ''}`}
                                                                        onClick={() => setSelectedTableName(tbl.name)}
                                                                    >
                                                                        <div className={styles.tableItemLeft}>
                                                                            <FaTable className={styles.tableItemIcon} />
                                                                            <div className={styles.tableItemText}>
                                                                                <span className={styles.tableName}>{tbl.name}</span>
                                                                                <span className={styles.tableSubtext}>
                                                                                    {tbl.columns_count} cols • {tbl.row_count} filas
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                        <button
                                                                            className={styles.quickEyeBtn}
                                                                            title="Previsualizar 10 filas"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleOpenSample(currentDb.name, tbl.name);
                                                                            }}
                                                                        >
                                                                            <FaEye size={12} />
                                                                        </button>
                                                                    </div>
                                                                );
                                                            })
                                                        )}
                                                    </div>
                                                </div>

                                                <div className={styles.tableDetailArea}>
                                                    {currentTable ? (
                                                        <div>
                                                            <div className={styles.detailHeader}>
                                                                <div>
                                                                    <div className={styles.detailTitleRow}>
                                                                        <h3 className={styles.detailTitle}>
                                                                            <span className={styles.dbPrefix}>{currentDb.name}.</span>{currentTable.name}
                                                                        </h3>
                                                                        <span className={styles.pillRows}>
                                                                            {currentTable.row_count} registros
                                                                        </span>
                                                                        <span className={styles.pillCols}>
                                                                            {currentTable.columns_count} columnas
                                                                        </span>
                                                                    </div>
                                                                    <p className={styles.detailDesc}>{currentTable.description}</p>
                                                                </div>

                                                                <div className={styles.detailActions}>
                                                                    <button
                                                                        className={styles.actionBtnSecondary}
                                                                        onClick={() => handleOpenSample(currentDb.name, currentTable.name)}
                                                                    >
                                                                        <FaEye /> Muestra de Datos
                                                                    </button>
                                                                    <button
                                                                        className={styles.actionBtnPrimary}
                                                                        onClick={() => handleSendToQuery(currentDb.name, currentTable)}
                                                                    >
                                                                        <FaTerminal /> Abrir en Consulta
                                                                    </button>
                                                                </div>
                                                            </div>

                                                            <div className={styles.dictTableWrapper}>
                                                                <table className={styles.dictTable}>
                                                                    <thead>
                                                                        <tr>
                                                                            <th style={{ width: '22%' }}>Columna</th>
                                                                            <th style={{ width: '18%' }}>Tipo de Dato</th>
                                                                            <th style={{ width: '15%' }}>Restricciones</th>
                                                                            <th style={{ width: '15%' }}>Valor Predeterminado</th>
                                                                            <th style={{ width: '30%' }}>Descripción / Propósito</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {(currentTable.columns || []).map(col => {
                                                                            const isColMatch = searchQuery && col.name.toLowerCase().includes(searchQuery.toLowerCase());
                                                                            return (
                                                                                <tr key={col.name} className={isColMatch ? styles.colHighlight : ''}>
                                                                                    <td className={styles.colNameCell}>
                                                                                        <span className={styles.colName}>{col.name}</span>
                                                                                        {col.is_primary_key && (
                                                                                            <span className={styles.badgePk} title="Llave Primaria">
                                                                                                <FaKey size={10} /> PK
                                                                                            </span>
                                                                                        )}
                                                                                        {col.is_foreign_key && (
                                                                                            <span className={styles.badgeFk} title={`Referencia a: ${col.foreign_key_target}`}>
                                                                                                <FaLink size={10} /> FK
                                                                                            </span>
                                                                                        )}
                                                                                    </td>
                                                                                    <td>
                                                                                        <span className={styles.typeCodeBadge}>{col.type}</span>
                                                                                    </td>
                                                                                    <td>
                                                                                        {col.nullable ? (
                                                                                            <span className={styles.badgeNull}>NULL</span>
                                                                                        ) : (
                                                                                            <span className={styles.badgeNotNull}>NOT NULL</span>
                                                                                        )}
                                                                                        {col.foreign_key_target && (
                                                                                            <div className={styles.fkTargetNotice}>
                                                                                                → {col.foreign_key_target}
                                                                                            </div>
                                                                                        )}
                                                                                    </td>
                                                                                    <td className={styles.defaultCell}>
                                                                                        {col.default ? (
                                                                                            <code className={styles.codeSnippet}>{col.default}</code>
                                                                                        ) : (
                                                                                            <span className={styles.mutedText}>—</span>
                                                                                        )}
                                                                                    </td>
                                                                                    <td className={styles.descCell}>
                                                                                        {col.description || (
                                                                                            <span className={styles.mutedText}>
                                                                                                {col.is_primary_key ? 'Identificador único del registro.' : 'Campo de datos de la entidad.'}
                                                                                            </span>
                                                                                        )}
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className={styles.emptyTableDetail}>
                                                            <FaTable size={36} style={{ opacity: 0.3 }} />
                                                            <p>Selecciona una tabla de la lista para ver su estructura.</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className={styles.emptyState}>
                                                <FaDatabase size={40} style={{ opacity: 0.3 }} />
                                                <p>No se encontraron bases de datos coincidentes con la búsqueda.</p>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {/* TAB 2: AI ACCESS CONTROL (datamart_db). */}
                        {activeTab === 'ai_control' && (
                            <div className={styles.aiControlRoot}>
                                <details className={styles.aiBanner} open={!isResponsiveDictionary}>
                                    <summary className={styles.aiBannerSummary}>
                                        <span className={styles.aiBannerIcon} aria-hidden="true">
                                            <FaShieldAlt size={26} />
                                        </span>
                                        <span className={styles.aiBannerText}>
                                            <span className={styles.aiBannerTitle} role="heading" aria-level="4">
                                                Gobierno de Acceso y Filtrado Dinámico para la IA
                                            </span>
                                        </span>
                                        <FaChevronDown className={styles.aiBannerChevron} aria-hidden="true" />
                                    </summary>
                                    <div className={styles.aiBannerBody}>
                                        <p>
                                            El microservicio de IA se conecta de forma exclusiva a Datamart en modo de <strong>estricto solo lectura</strong>.
                                            Aquí puedes activar, desactivar y ajustar dinámicamente qué tablas, columnas y filtros WHERE están autorizados para responder preguntas ciudadanas.
                                        </p>
                                    </div>
                                </details>

                                {loadingAiRules ? (
                                    <div className={styles.loadingContainer}>
                                        <FaSpinner className={styles.spinIcon} size={32} />
                                        <p>Cargando catálogo de consultas autorizadas desde datamart_db...</p>
                                    </div>
                                ) : aiRules.length === 0 ? (
                                    <div className={styles.emptyState}>
                                        <FaRobot size={40} style={{ opacity: 0.3, marginBottom: '1rem' }} />
                                        <p>No hay reglas de acceso registradas para la IA.</p>
                                        <button className={styles.actionBtnPrimary} onClick={handleStartCreateAiInBuilder} style={{ marginTop: '1rem' }}>
                                            <FaTerminal /> Crear Consulta con Constructor Dinámico
                                        </button>
                                    </div>
                                ) : (
                                    <div
                                        className={styles.aiRulesGrid}
                                        role={isResponsiveDictionary ? 'region' : undefined}
                                        aria-label={isResponsiveDictionary ? 'Reglas de acceso IA' : undefined}
                                        tabIndex={isResponsiveDictionary ? 0 : undefined}
                                    >
                                        {aiRules.map(rule => {
                                            const isToggling = togglingRuleId === rule.id;
                                            return (
                                                <div
                                                    key={rule.id}
                                                    className={`${styles.aiRuleCard} ${!rule.is_enabled ? styles.aiRuleCardDisabled : ''}`}
                                                >
                                                    <div className={styles.aiRuleHeader}>
                                                        <div className={styles.aiRuleTitleGroup}>
                                                            <h4 className={styles.aiRuleName}>{rule.name}</h4>
                                                            <div className={styles.aiRuleBadges}>
                                                                <span className={styles.aiRuleCodeBadge}><code>{rule.code}</code></span>
                                                                <span className={styles.aiRuleDbBadge}>
                                                                    <FaDatabase size={11} /> {rule.target_db}.{rule.target_table}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        {/* Enable/disable toggle. */}
                                                        <button
                                                            type="button"
                                                            className={`${styles.aiSwitchBtn} ${rule.is_enabled ? styles.aiSwitchActive : styles.aiSwitchInactive}`}
                                                            onClick={() => handleToggleRule(rule)}
                                                            disabled={isToggling}
                                                            aria-pressed={rule.is_enabled}
                                                            title={rule.is_enabled ? "Haz clic para desactivar acceso de IA a este contexto" : "Haz clic para permitir acceso de IA a este contexto"}
                                                        >
                                                            {isToggling ? (
                                                                <FaSpinner className={styles.spinIcon} size={14} />
                                                            ) : rule.is_enabled ? (
                                                                <>
                                                                    <FaToggleOn size={22} />
                                                                    <span>Habilitado</span>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <FaToggleOff size={22} />
                                                                    <span>Bloqueado</span>
                                                                </>
                                                            )}
                                                        </button>
                                                    </div>

                                                    <p className={styles.aiRuleDesc}>
                                                        {rule.description || "Conjunto de datos consultable por el Asistente IA."}
                                                    </p>

                                                    {/* Authorized columns (whitelist). */}
                                                    <div className={styles.aiRuleSection}>
                                                        <span className={styles.aiSectionLabel}>
                                                            <FaColumns size={12} /> Columnas Autorizadas ({Array.isArray(rule.selected_columns) ? rule.selected_columns.length : 1}):
                                                        </span>
                                                        <div className={styles.aiColChips}>
                                                            {Array.isArray(rule.selected_columns) ? (
                                                                rule.selected_columns.map(c => (
                                                                    <span key={c} className={styles.aiColChip}>
                                                                        {c}
                                                                    </span>
                                                                ))
                                                            ) : (
                                                                <span className={styles.aiColChip}>* (Todas)</span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Dynamic WHERE filters. */}
                                                    <div className={styles.aiRuleSection}>
                                                        <span className={styles.aiSectionLabel}>
                                                            <FaFilter size={12} /> Filtro Dinámico (WHERE):
                                                        </span>
                                                        <code className={styles.aiFilterCode}>
                                                            {renderFilterRulesText(rule.filter_rules)}
                                                        </code>
                                                    </div>

                                                    <div className={styles.aiRuleFooter}>
                                                        <span className={styles.aiLimitText}>
                                                            Límite: {rule.max_rows || 100} filas
                                                        </span>

                                                        <div className={styles.aiCardActions}>
                                                            <button
                                                                type="button"
                                                                className={styles.aiTestBtn}
                                                                onClick={() => handleTestAiQuery(rule)}
                                                                title="Probar consulta y ver qué datos recibe la IA"
                                                            >
                                                                <FaEye /> Probar
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className={styles.aiEditBtn}
                                                                onClick={() => handleEditInBuilder(rule)}
                                                                title="Abrir y modificar esta consulta en el Constructor Dinámico"
                                                            >
                                                                <FaTerminal /> Editar en Constructor
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className={styles.aiDeleteBtn}
                                                                onClick={() => handleDeleteRule(rule.id)}
                                                                aria-label={`Eliminar regla ${rule.name}`}
                                                                title="Eliminar regla"
                                                            >
                                                                <FaTrash />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* TAB 3: PREDEFINED VIEWS — visual cards. */}
                        {activeTab === 'views' && (
                            <div className={styles.viewsRoot}>
                                {selectedView ? (
                                    <section className={styles.viewRecordsArea}>
                                        <header className={styles.viewRecordsHeader}>
                                            <button
                                                type="button"
                                                className={styles.viewBackButton}
                                                onClick={() => setSelectedView(null)}
                                            >
                                                <FaArrowLeft aria-hidden="true" />
                                                Volver a vistas
                                            </button>
                                            <div className={styles.viewRecordsHeading}>
                                                <h3 className={styles.viewRecordsTitle}>
                                                    {PREDEFINED_VIEWS.find(view => view.id === selectedView)?.label || selectedView}
                                                </h3>
                                                {!loadingView && !viewError && (
                                                    <span className={styles.viewResultsBadge}>
                                                        {viewsData.length} registros
                                                    </span>
                                                )}
                                            </div>
                                        </header>

                                        {loadingView ? (
                                            <div className={styles.loadingContainer} role="status">
                                                <FaSpinner className={styles.spinIcon} size={28} />
                                                <p>Cargando datos de la vista...</p>
                                            </div>
                                        ) : viewError ? (
                                            <div className={styles.viewRecordsMessage} role="alert">
                                                {viewError}
                                            </div>
                                        ) : viewsData.length === 0 ? (
                                            <div className={styles.viewRecordsMessage} role="status">
                                                Esta vista no contiene registros.
                                            </div>
                                        ) : (
                                            <div
                                                className={styles.viewRecordsList}
                                                role="list"
                                                aria-label={`Registros de ${PREDEFINED_VIEWS.find(view => view.id === selectedView)?.label || selectedView}`}
                                            >
                                                {viewsData.map((record, index) => {
                                                    const fields = getViewRecordFields(record);
                                                    const summaryFields = getViewRecordSummaryFields(fields);

                                                    return (
                                                        <details
                                                            key={`${selectedView}-${index}`}
                                                            className={styles.viewRecordCard}
                                                            role="listitem"
                                                        >
                                                            <summary className={styles.viewRecordSummary}>
                                                                <span className={styles.viewRecordIndex}>
                                                                    Registro {index + 1}
                                                                </span>
                                                                <span className={styles.viewRecordHighlights}>
                                                                    {summaryFields.map(([name, value]) => (
                                                                        <span className={styles.viewRecordHighlight} key={name}>
                                                                            <strong>{name}:</strong> {formatViewValue(value)}
                                                                        </span>
                                                                    ))}
                                                                </span>
                                                                <FaChevronDown className={styles.viewRecordChevron} aria-hidden="true" />
                                                            </summary>
                                                            <dl className={styles.viewRecordFields}>
                                                                {fields.map(([name, value]) => (
                                                                    <div className={styles.viewRecordField} key={name}>
                                                                        <dt>{name}</dt>
                                                                        <dd>{formatViewValue(value)}</dd>
                                                                    </div>
                                                                ))}
                                                            </dl>
                                                        </details>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </section>
                                ) : (
                                    <>
                                        <div className={styles.viewsBanner}>
                                            <FaLayerGroup style={{ color: '#6366F1', fontSize: '1.25rem' }} />
                                            <div>
                                                <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1E1B4B' }}>Vistas Rápidas del Sistema</h4>
                                                <p style={{ margin: 0, fontSize: '0.82rem', color: '#4B5563' }}>Acceso de un clic a conjuntos de datos estratégicos del sistema. Haz clic en una tarjeta para cargar los datos.</p>
                                            </div>
                                        </div>

                                        <div className={styles.viewsGrid}>
                                            {PREDEFINED_VIEWS.map(view => {
                                                const IconComp = view.icon;
                                                return (
                                                    <button
                                                        type="button"
                                                        key={view.id}
                                                        className={styles.viewCard}
                                                        onClick={() => handleFetchView(view.id)}
                                                    >
                                                        <span className={styles.viewCardIcon} style={{ background: view.bg, color: view.color }}>
                                                            <IconComp size={20} aria-hidden="true" />
                                                        </span>
                                                        <span className={styles.viewCardContent}>
                                                            <span className={styles.viewCardTitle}>{view.label}</span>
                                                            <span className={styles.viewCardMeta}>
                                                                <code style={{ background: '#F3F4F6', padding: '0.1rem 0.3rem', borderRadius: 3, fontSize: '0.72rem' }}>
                                                                    {view.db}
                                                                </code>
                                                                <span style={{ color: '#9CA3AF' }}> · </span>
                                                                <code style={{ background: '#F3F4F6', padding: '0.1rem 0.3rem', borderRadius: 3, fontSize: '0.72rem' }}>
                                                                    {view.table}
                                                                </code>
                                                            </span>
                                                            <span className={styles.viewCardDesc}>{view.description}</span>
                                                        </span>
                                                        <span className={styles.viewCardAction} style={{ color: view.color }}>
                                                            <FaChevronRight size={14} aria-hidden="true" />
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* TAB 4: VISUAL QUERY BUILDER. */}
                        {activeTab === 'queries' && (
                            <div className={styles.queryBuilderRoot}>
                                {/* Top toolbar with prominent AI save button. */}
                                <div className={styles.qbTopToolbar}>
                                    <div className={styles.qbTopToolbarTitle}>
                                        <FaRobot size={20} color="#4F46E5" />
                                        <div>
                                            <div style={{ fontWeight: 700, fontSize: '0.98rem' }}>
                                                Constructor Dinámico de Consultas
                                            </div>
                                            <div style={{ fontSize: '0.78rem', color: '#6B7280', fontWeight: 400 }}>
                                                Diseña la consulta visualmente y guárdala para que el Asistente IA la use en el chat
                                            </div>
                                        </div>
                                    </div>
                                    {!isResponsiveDictionary && (
                                        <div className={styles.qbTopToolbarActions}>
                                            <button
                                                type="button"
                                                className={styles.saveAiMainBtn}
                                                style={{ margin: 0, width: 'auto', padding: '0.65rem 1.25rem', fontSize: '0.88rem' }}
                                                onClick={handleSaveAiConfigDirect}
                                                disabled={saveAiLoading || !builder.selectedDb || !builder.selectedTable}
                                                id="btn-save-ai-query-top"
                                                title="Guardar la configuración de esta consulta para que el Asistente IA la consulte"
                                            >
                                                {saveAiLoading ? <FaSpinner className={styles.spinIcon} /> : <FaRobot size={15} />}
                                                Guardar Configuración de Consulta para que Consulte la IA
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Builder mode banner for the AI assistant. */}
                                {builderAiContext && (
                                    <div className={styles.qbAiBanner}>
                                        <div className={styles.qbAiBannerContent}>
                                            <div className={styles.qbAiBannerIcon}>
                                                <FaRobot size={20} />
                                            </div>
                                            <div className={styles.qbAiBannerText}>
                                                <strong>Modo Constructor para Asistente IA</strong>
                                                <p>
                                                    {builderAiContext.isNew
                                                        ? 'Diseña la consulta visualmente y al terminar presiona "Vincular Consulta a la IA" para habilitar este contexto.'
                                                        : `Editando consulta de la regla: ${builderAiContext.name} (${builderAiContext.code})`}
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            className={styles.qbAiBannerClose}
                                            onClick={() => setBuilderAiContext(null)}
                                        >
                                            Salir de Modo IA
                                        </button>
                                    </div>
                                )}

                                {isResponsiveDictionary && (
                                    <div className={styles.qbStageTabs} role="tablist" aria-label="Etapas del Constructor Dinámico">
                                        {BUILDER_RESPONSIVE_STAGES.map((stage, index) => {
                                            const isActive = activeBuilderStage === stage.id;
                                            const isDisabled = stage.id !== 'data' && !builder.selectedTable;
                                            const controlledPanels = stage.id === 'query'
                                                ? 'qb-stage-panel-query qb-stage-panel-results'
                                                : `qb-stage-panel-${stage.id}`;
                                            return (
                                                <button
                                                    key={stage.id}
                                                    id={`qb-stage-tab-${stage.id}`}
                                                    type="button"
                                                    role="tab"
                                                    aria-selected={isActive}
                                                    aria-controls={controlledPanels}
                                                    aria-disabled={isDisabled}
                                                    tabIndex={isActive ? 0 : -1}
                                                    disabled={isDisabled}
                                                    className={`${styles.qbStageTab} ${isActive ? styles.qbStageTabActive : ''}`}
                                                    onClick={() => setActiveBuilderStage(stage.id)}
                                                    onKeyDown={(event) => handleBuilderStageKeyDown(event, stage.id)}
                                                >
                                                    <span className={styles.qbStageNumber}>{index + 1}</span>
                                                    <span>{stage.label}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}

                                <div
                                    id="qb-stage-panel-data"
                                    className={`${styles.qbResponsiveStagePanel} ${styles.qbStagePanelData}`}
                                    role={isResponsiveDictionary ? 'tabpanel' : undefined}
                                    aria-labelledby={isResponsiveDictionary ? 'qb-stage-tab-data' : undefined}
                                    hidden={isResponsiveDictionary && activeBuilderStage !== 'data'}
                                >
                                {/* STEP 1: Select database. */}
                                <div className={styles.qbSection}>
                                    <div className={styles.qbSectionHeader}>
                                        <span className={styles.qbStepBadge}>1</span>
                                        <span className={styles.qbSectionTitle}>Base de Datos</span>
                                        {builder.selectedDb && <span className={styles.qbSelectedBadge}>{builder.selectedDb}</span>}
                                    </div>
                                    {isResponsiveDictionary ? (
                                        <div className={styles.responsiveDbSelectWrap}>
                                            <FaDatabase className={styles.responsiveDbIcon} aria-hidden="true" />
                                            <select
                                                className={styles.responsiveDbSelect}
                                                value={builder.selectedDb}
                                                onChange={(event) => handleBuilderDbSelect(event.currentTarget.value)}
                                                aria-label="Base de datos del Constructor"
                                                disabled={dictionaries.length === 0}
                                            >
                                                {dictionaries.length === 0 ? (
                                                    <option value="">
                                                        {loadingDict ? 'Cargando bases de datos...' : 'No hay bases de datos disponibles'}
                                                    </option>
                                                ) : (
                                                    <>
                                                        <option value="">Selecciona una base de datos</option>
                                                        {builder.selectedDb && !dictionaries.some(db => db.name === builder.selectedDb) && (
                                                            <option value={builder.selectedDb}>
                                                                {builder.selectedDb} (no disponible)
                                                            </option>
                                                        )}
                                                        {dictionaries.map(db => (
                                                            <option key={db.name} value={db.name}>{db.name}</option>
                                                        ))}
                                                    </>
                                                )}
                                            </select>
                                            <FaChevronDown className={styles.responsiveDbChevron} aria-hidden="true" />
                                        </div>
                                    ) : (
                                        <div className={styles.qbDbPills}>
                                            {dictionaries.length === 0 ? (
                                                <div className={styles.qbEmptyNote}>
                                                    <FaSpinner className={styles.spinIcon} /> Cargando bases de datos...
                                                </div>
                                            ) : dictionaries.map(db => (
                                                <button
                                                    type="button"
                                                    key={db.name}
                                                    className={`${styles.qbDbPill} ${builder.selectedDb === db.name ? styles.qbDbPillActive : ''}`}
                                                    onClick={() => handleBuilderDbSelect(db.name)}
                                                >
                                                    <FaDatabase style={{ fontSize: '0.8rem' }} />
                                                    {db.name}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* STEP 2: Select table. */}
                                {builder.selectedDb && (
                                    <div className={styles.qbSection}>
                                        <div className={styles.qbSectionHeader}>
                                            <span className={styles.qbStepBadge}>2</span>
                                            <span className={styles.qbSectionTitle}>Tabla</span>
                                            {builder.selectedTable && <span className={styles.qbSelectedBadge}>{builder.selectedTable}</span>}
                                        </div>
                                        {isResponsiveDictionary ? (
                                            builderTables.length === 0 ? (
                                                <div className={styles.qbEmptyNote} role="status">
                                                    Esta base de datos no tiene tablas de dominio.
                                                </div>
                                            ) : (
                                                <div
                                                    className={styles.qbResponsiveTableMenu}
                                                    role="region"
                                                    aria-label={`Tablas disponibles de ${builder.selectedDb}`}
                                                    tabIndex={0}
                                                >
                                                    {builderTables.map(tbl => (
                                                        <button
                                                            type="button"
                                                            key={tbl.name}
                                                            className={`${styles.responsiveTableCard} ${builder.selectedTable === tbl.name ? styles.responsiveTableCardActive : ''}`}
                                                            onClick={() => handleBuilderTableSelect(tbl.name)}
                                                            aria-pressed={builder.selectedTable === tbl.name}
                                                        >
                                                            <span className={styles.responsiveTableIcon}>
                                                                <FaTable aria-hidden="true" />
                                                            </span>
                                                            <span className={styles.responsiveTableInfo}>
                                                                <span className={styles.responsiveTableName}>{tbl.name}</span>
                                                                <span className={styles.responsiveTableMeta}>
                                                                    {tbl.columns_count ?? tbl.columns?.length ?? 0} columnas
                                                                </span>
                                                            </span>
                                                            <FaChevronRight className={styles.responsiveTableChevron} aria-hidden="true" />
                                                        </button>
                                                    ))}
                                                </div>
                                            )
                                        ) : (
                                            <div className={styles.qbTableGrid}>
                                                {builderTables.length === 0 ? (
                                                    <div className={styles.qbEmptyNote}>Esta base de datos no tiene tablas de dominio.</div>
                                                ) : builderTables.map(tbl => (
                                                    <button
                                                        type="button"
                                                        key={tbl.name}
                                                        className={`${styles.qbTableBtn} ${builder.selectedTable === tbl.name ? styles.qbTableBtnActive : ''}`}
                                                        onClick={() => handleBuilderTableSelect(tbl.name)}
                                                        aria-pressed={builder.selectedTable === tbl.name}
                                                    >
                                                        <FaTable style={{ fontSize: '0.75rem', marginRight: '5px' }} />
                                                        <span className={styles.qbTableBtnName}>{tbl.name}</span>
                                                        <span className={styles.qbTableBtnMeta}>{(tbl.columns || []).length} cols</span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* STEP 3: Select columns. */}
                                {builder.selectedTable && builderColumns.length > 0 && (
                                    <div className={styles.qbSection}>
                                        <div className={styles.qbSectionHeader}>
                                            <span className={styles.qbStepBadge}>3</span>
                                            <span className={styles.qbSectionTitle}>Columnas</span>
                                            <span
                                                className={styles.qbColumnSelectionCount}
                                                role="status"
                                                aria-live="polite"
                                                aria-label={`${selectedColumnCount} de ${builderColumns.length} columnas seleccionadas`}
                                            >
                                                {selectedColumnCount} de {builderColumns.length} seleccionadas
                                            </span>
                                            <button className={styles.qbLinkBtn} onClick={handleSelectAllColumns}>
                                                Seleccionar todas (*)
                                            </button>
                                        </div>
                                        <div
                                            className={styles.qbColGrid}
                                            role="group"
                                            aria-label={`Columnas disponibles para ${builder.selectedTable}`}
                                        >
                                            {builderColumns.map(col => {
                                                const isSelected = builder.selectedColumns.length === 0 || builder.selectedColumns.includes(col.name);
                                                const isExplicit = builder.selectedColumns.includes(col.name);
                                                const isAll = builder.selectedColumns.length === 0;
                                                return (
                                                    <label
                                                        key={col.name}
                                                        className={`${styles.qbColChip} ${isAll || isExplicit ? styles.qbColChipOn : ''}`}
                                                        title={`${col.type}${col.nullable ? ' (nullable)' : ' NOT NULL'}${col.is_primary_key ? ' · PK' : ''}${col.is_foreign_key ? ' · FK' : ''}`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            style={{ display: 'none' }}
                                                            checked={isAll || isExplicit}
                                                            onChange={() => {
                                                                if (isAll) {
                                                                    // When all columns are selected, unchecking one switches to explicit selection mode.
                                                                    setBuilder(prev => ({
                                                                        ...prev,
                                                                        selectedColumns: builderColumns.filter(c => c.name !== col.name).map(c => c.name)
                                                                    }));
                                                                } else {
                                                                    handleToggleColumn(col.name);
                                                                }
                                                            }}
                                                        />
                                                        {col.is_primary_key && <FaKey size={9} style={{ color: '#D97706', marginRight: 3 }} />}
                                                        {col.is_foreign_key && <FaLink size={9} style={{ color: '#2563EB', marginRight: 3 }} />}
                                                        <span className={styles.qbColName}>{col.name}</span>
                                                        <span className={styles.qbColType}>{col.type}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                </div>

                                <div
                                    id="qb-stage-panel-query"
                                    className={`${styles.qbResponsiveStagePanel} ${styles.qbStagePanelQuery}`}
                                    role={isResponsiveDictionary ? 'tabpanel' : undefined}
                                    aria-labelledby={isResponsiveDictionary ? 'qb-stage-tab-query' : undefined}
                                    hidden={isResponsiveDictionary && activeBuilderStage !== 'query'}
                                >
                                {/* STEP 4: WHERE filters. */}
                                {builder.selectedTable && (
                                    <div className={styles.qbSection}>
                                        <div className={styles.qbSectionHeader}>
                                            <span className={styles.qbStepBadge}>4</span>
                                            <span className={styles.qbSectionTitle}>Filtros WHERE</span>
                                            <button
                                                className={styles.qbAddFilterBtn}
                                                onClick={handleAddFilter}
                                                disabled={builderColumns.length === 0}
                                            >
                                                <FaPlus size={10} /> Agregar filtro
                                            </button>
                                        </div>
                                        {builder.filters.length === 0 ? (
                                            <div className={styles.qbEmptyNote}>
                                                Sin filtros &mdash; se devolverán todos los registros.
                                            </div>
                                        ) : (
                                            <div className={styles.qbFilterRows}>
                                                {builder.filters.map((filter, idx) => (
                                                    <div key={filter.id} className={styles.qbFilterRow}>
                                                        <span className={styles.qbFilterConnector}>{idx === 0 ? 'WHERE' : 'AND'}</span>
                                                        <select
                                                            className={styles.qbFilterSelect}
                                                            value={filter.column}
                                                            onChange={e => handleFilterChange(filter.id, 'column', e.target.value)}
                                                        >
                                                            {builderColumns.map(col => (
                                                                <option key={col.name} value={col.name}>{col.name}</option>
                                                            ))}
                                                        </select>
                                                        <select
                                                            className={styles.qbFilterSelect}
                                                            style={{ maxWidth: '170px' }}
                                                            value={filter.operator}
                                                            onChange={e => handleFilterChange(filter.id, 'operator', e.target.value)}
                                                        >
                                                            {OPERATORS.map(op => (
                                                                <option key={op.value} value={op.value}>{op.label}</option>
                                                            ))}
                                                        </select>
                                                        {!NO_VALUE_OPS.includes(filter.operator) && (
                                                            <input
                                                                type="text"
                                                                className={styles.qbFilterInput}
                                                                placeholder="valor..."
                                                                value={filter.value}
                                                                onChange={e => handleFilterChange(filter.id, 'value', e.target.value)}
                                                            />
                                                        )}
                                                        <button
                                                            className={styles.qbRemoveFilterBtn}
                                                            onClick={() => handleRemoveFilter(filter.id)}
                                                            title="Eliminar filtro"
                                                        >
                                                            <FaTimes size={11} />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* STEP 5: Limit & Offset */}
                                {builder.selectedTable && (
                                    <div className={styles.qbSection}>
                                        <div className={styles.qbSectionHeader}>
                                            <span className={styles.qbStepBadge}>5</span>
                                            <span className={styles.qbSectionTitle}>Paginación</span>
                                        </div>
                                        <div className={styles.qbPaginationRow}>
                                            <label className={styles.qbPaginationLabel}>
                                                Límite de filas
                                                <input
                                                    type="number"
                                                    className={styles.qbNumberInput}
                                                    min={1} max={1000}
                                                    value={builder.limit}
                                                    onChange={e => setBuilder(prev => ({ ...prev, limit: parseInt(e.target.value, 10) || 50 }))}
                                                />
                                            </label>
                                            <label className={styles.qbPaginationLabel}>
                                                Offset
                                                <input
                                                    type="number"
                                                    className={styles.qbNumberInput}
                                                    min={0}
                                                    value={builder.offset}
                                                    onChange={e => setBuilder(prev => ({ ...prev, offset: parseInt(e.target.value, 10) || 0 }))}
                                                />
                                            </label>
                                        </div>
                                    </div>
                                )}

                                </div>

                                <div
                                    id="qb-stage-panel-ai"
                                    className={`${styles.qbResponsiveStagePanel} ${styles.qbStagePanelAi}`}
                                    role={isResponsiveDictionary ? 'tabpanel' : undefined}
                                    aria-labelledby={isResponsiveDictionary ? 'qb-stage-tab-ai' : undefined}
                                    hidden={isResponsiveDictionary && activeBuilderStage !== 'ai'}
                                >
                                {/* STEP 6: Configure the query for AI use. */}
                                {builder.selectedTable && (
                                    <div className={styles.qbAiSection}>
                                        <div className={styles.qbSectionHeader}>
                                            <span className={styles.qbStepBadge} style={{ background: '#4F46E5' }}>6</span>
                                            <span className={styles.qbSectionTitle} style={{ color: '#312E81' }}>
                                                Configuración de Consulta para el Asistente IA
                                            </span>
                                            <span className={builderAiForm.is_enabled ? styles.badgeSuccess : styles.badgeMuted}>
                                                {builderAiForm.is_enabled ? '✓ Habilitada para Chat IA' : '✕ Bloqueada para IA'}
                                            </span>
                                        </div>
                                        <p style={{ fontSize: '0.84rem', color: '#4B5563', margin: '0.5rem 0 0.85rem 0' }}>
                                            Define cómo el microservicio de IA (<code>ms-ai</code>) utilizará esta consulta para responder a las preguntas ciudadanas en el chat con datos reales.
                                        </p>

                                        {/* AI availability toggle. */}
                                        <div style={{ marginBottom: '1rem' }}>
                                            <button
                                                type="button"
                                                className={`${builderAiForm.is_enabled ? styles.aiToggleBtnActive : styles.aiToggleBtnInactive} ${styles.qbAiAvailabilityToggle}`}
                                                onClick={() => setBuilderAiForm(prev => ({ ...prev, is_enabled: !prev.is_enabled }))}
                                                aria-pressed={builderAiForm.is_enabled}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                    {builderAiForm.is_enabled ? (
                                                        <FaToggleOn size={26} color="#10B981" />
                                                    ) : (
                                                        <FaToggleOff size={26} color="#EF4444" />
                                                    )}
                                                    <div>
                                                        <div style={{ fontWeight: 700 }}>
                                                            {builderAiForm.is_enabled
                                                                ? '✓ HABILITADA: El Asistente IA puede consultar estos datos'
                                                                : '✕ DESHABILITADA: El Asistente IA tiene bloqueado el acceso a estos datos'}
                                                        </div>
                                                        <div style={{ fontSize: '0.78rem', color: builderAiForm.is_enabled ? '#065F46' : '#991B1B' }}>
                                                            {builderAiForm.is_enabled
                                                                ? 'La IA usará las columnas y filtros configurados en esta consulta para responder en el chat.'
                                                                : 'La IA responderá que no tiene información disponible sobre este tema.'}
                                                        </div>
                                                    </div>
                                                </div>
                                                <span style={{ fontSize: '0.8rem', fontWeight: 600, textDecoration: 'underline' }}>
                                                    {builderAiForm.is_enabled ? 'Clic para Desactivar' : 'Clic para Activar'}
                                                </span>
                                            </button>
                                        </div>

                                        <div className={styles.qbAiFormGrid}>
                                            <div>
                                                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' }}>
                                                    Nombre Descriptivo de la Consulta *
                                                </label>
                                                <input
                                                    type="text"
                                                    className={styles.modalInput}
                                                    value={builderAiForm.name}
                                                    placeholder="Ej. Consulta de Certificados Emitidos"
                                                    onChange={e => setBuilderAiForm(prev => ({ ...prev, name: e.target.value }))}
                                                />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' }}>
                                                    Código Identificador RAG (clave interna) *
                                                </label>
                                                <input
                                                    type="text"
                                                    className={styles.modalInput}
                                                    value={builderAiForm.code}
                                                    placeholder="Ej. certificados_emitidos"
                                                    onChange={e => setBuilderAiForm(prev => ({ ...prev, code: e.target.value }))}
                                                />
                                            </div>
                                            <div style={{ gridColumn: '1 / -1' }}>
                                                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' }}>
                                                    Descripción / Contexto Semántico para la IA
                                                </label>
                                                <textarea
                                                    className={styles.modalInput}
                                                    rows={2}
                                                    value={builderAiForm.description}
                                                    placeholder="Describe para qué sirve esta consulta o qué preguntas responde (ej: folios emitidos, fechas, estatus)..."
                                                    onChange={e => setBuilderAiForm(prev => ({ ...prev, description: e.target.value }))}
                                                />
                                            </div>
                                        </div>

                                        {/* Primary save configuration button. */}
                                        <button
                                            type="button"
                                            className={styles.saveAiMainBtn}
                                            onClick={handleSaveAiConfigDirect}
                                            disabled={saveAiLoading || !builder.selectedDb || !builder.selectedTable}
                                            id="btn-save-ai-query-step6"
                                        >
                                            {saveAiLoading ? (
                                                <><FaSpinner className={styles.spinIcon} /> Guardando configuración en datamart...</>
                                            ) : (
                                                <><FaRobot size={18} /> Guardar Configuración de Consulta para que Consulte la IA</>
                                            )}
                                        </button>
                                    </div>
                                )}

                                </div>

                                <div
                                    id="qb-stage-panel-results"
                                    className={`${styles.qbResponsiveStagePanel} ${styles.qbStagePanelResults}`}
                                    role={isResponsiveDictionary ? 'region' : undefined}
                                    aria-labelledby={isResponsiveDictionary ? 'qb-stage-tab-query' : undefined}
                                    hidden={isResponsiveDictionary && activeBuilderStage !== 'query'}
                                >
                                {/* SQL Preview */}
                                <div className={styles.qbSqlPreviewContainer}>
                                    <div className={styles.qbSqlPreviewHeader}>
                                        <button
                                            className={styles.qbSqlToggleBtn}
                                            onClick={() => setShowSqlPreview(prev => !prev)}
                                        >
                                            <FaCode size={13} />
                                            {showSqlPreview ? 'Ocultar' : 'Ver'} SQL Generado
                                            {showSqlPreview ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
                                        </button>
                                        {showSqlPreview && (
                                            <button className={styles.qbCopyBtn} onClick={handleCopySql}>
                                                <FaCopy size={12} /> Copiar
                                            </button>
                                        )}
                                    </div>
                                    {showSqlPreview && (
                                        <pre className={styles.qbSqlPreview}>{sqlPreview}</pre>
                                    )}
                                </div>

                                {/* Execute & Save to AI Buttons */}
                                <div className={styles.qbExecuteRow}>
                                    <button
                                        className={styles.qbExecuteBtn}
                                        onClick={handleRunBuilderQuery}
                                        disabled={queryLoading || !builder.selectedDb || !builder.selectedTable}
                                    >
                                        {queryLoading
                                            ? <><FaSpinner className={styles.spinIcon} /> Ejecutando...</>
                                            : <><FaPlay /> Ejecutar Consulta</>
                                        }
                                    </button>
                                    {!isResponsiveDictionary && (
                                        <button
                                            type="button"
                                            className={`${styles.saveAiMainBtn} ${styles.qbSaveAiBottomBtn}`}
                                            style={{ width: 'auto', padding: '0.65rem 1.35rem', margin: 0 }}
                                            onClick={handleSaveAiConfigDirect}
                                            disabled={saveAiLoading || !builder.selectedDb || !builder.selectedTable}
                                            id="btn-save-ai-query-bottom"
                                            title="Guardar la configuración de esta consulta para que el Asistente IA la consulte"
                                        >
                                            {saveAiLoading ? <FaSpinner className={styles.spinIcon} /> : <FaRobot />}
                                            Guardar Configuración de Consulta para que Consulte la IA
                                        </button>
                                    )}
                                    {queryData.length > 0 && (
                                        <button
                                            className={styles.qbClearBtn}
                                            onClick={() => setQueryData([])}
                                        >
                                            <FaEraser /> Limpiar resultados
                                        </button>
                                    )}
                                    {queryData.length > 0 && (
                                        <span className={styles.qbResultCount}>
                                            {queryData.length} registro(s)
                                        </span>
                                    )}
                                </div>

                                {/* Results */}
                                {queryData.length > 0 && (
                                    <div className={styles.qbResults}>
                                        {renderTable(queryData)}
                                    </div>
                                )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* MODAL 1: SAMPLE PREVIEW (DICTIONARY). */}
                {sampleModal.isOpen && (
                    <div className={styles.modalOverlay} onClick={() => setSampleModal(prev => ({ ...prev, isOpen: false }))}>
                        <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
                            <div className={styles.modalHeader}>
                                <div className={styles.modalTitleBox}>
                                    <FaEye style={{ color: 'var(--color-primary)' }} />
                                    <h3 className={styles.modalTitle}>
                                        Muestra de Datos: <span className={styles.dbPrefix}>{sampleModal.db}.</span>{sampleModal.table}
                                    </h3>
                                    <span className={styles.modalBadge}>Primeras 10 filas</span>
                                </div>
                                <button
                                    className={styles.closeBtn}
                                    onClick={() => setSampleModal(prev => ({ ...prev, isOpen: false }))}
                                >
                                    <FaTimes />
                                </button>
                            </div>

                            <div className={styles.modalBody}>
                                {sampleModal.loading ? (
                                    <div className={styles.loadingContainer}>
                                        <FaSpinner className={styles.spinIcon} size={28} />
                                        <p>Consultando muestra de datos...</p>
                                    </div>
                                ) : sampleModal.error ? (
                                    <div className={styles.emptyState}>
                                        <p style={{ color: 'var(--color-danger)' }}>{sampleModal.error}</p>
                                    </div>
                                ) : sampleModal.data.length === 0 ? (
                                    <div className={styles.emptyState}>
                                        <p>La tabla no contiene registros actualmente.</p>
                                    </div>
                                ) : (
                                    renderTable(sampleModal.data)
                                )}
                            </div>

                            <div className={styles.modalFooter}>
                                <button
                                    className={styles.secondaryActionBtn}
                                    onClick={() => setSampleModal(prev => ({ ...prev, isOpen: false }))}
                                >
                                    Cerrar
                                </button>
                                {sampleModal.data.length > 0 && currentTable && (
                                    <button
                                        className={styles.actionBtnPrimary}
                                        onClick={() => {
                                            setSampleModal(prev => ({ ...prev, isOpen: false }));
                                            handleSendToQuery(sampleModal.db, currentTable);
                                        }}
                                    >
                                        <FaTerminal /> Abrir en Consultas Dinámicas
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* MODAL 2: AI QUERY TEST (WHAT THE AI SEES). */}
                {aiTestModal.isOpen && (
                    <div className={styles.modalOverlay} onClick={() => setAiTestModal(prev => ({ ...prev, isOpen: false }))}>
                        <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
                            <div className={styles.modalHeader}>
                                <div className={styles.modalTitleBox}>
                                    <FaRobot style={{ color: 'var(--color-primary)' }} />
                                    <h3 className={styles.modalTitle}>
                                        Resultado de Consulta para IA: <span className={styles.dbPrefix}>{aiTestModal.ruleName}</span>
                                    </h3>
                                    <span className={styles.modalBadge}>Código: {aiTestModal.queryCode}</span>
                                </div>
                                <button
                                    className={styles.closeBtn}
                                    onClick={() => setAiTestModal(prev => ({ ...prev, isOpen: false }))}
                                >
                                    <FaTimes />
                                </button>
                            </div>

                            <div className={styles.modalBody}>
                                {aiTestModal.loading ? (
                                    <div className={styles.loadingContainer}>
                                        <FaSpinner className={styles.spinIcon} size={28} />
                                        <p>Ejecutando consulta en modo solo lectura para la IA...</p>
                                    </div>
                                ) : aiTestModal.error ? (
                                    <div className={styles.emptyState}>
                                        <FaBan size={36} style={{ color: 'var(--color-danger)', marginBottom: '0.75rem' }} />
                                        <p style={{ color: 'var(--color-danger)', fontWeight: 600 }}>{aiTestModal.error}</p>
                                    </div>
                                ) : aiTestModal.data.length === 0 ? (
                                    <div className={styles.emptyState}>
                                        <p>La consulta no arrojó registros con los filtros actuales.</p>
                                    </div>
                                ) : (
                                    <div>
                                        <div style={{ marginBottom: '1rem', fontSize: '0.85rem', color: '#059669', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <FaCheckCircle /> <span>{aiTestModal.data.length} registros devueltos a la IA (columnas protegidas y filtros aplicados).</span>
                                        </div>
                                        {renderTable(aiTestModal.data)}
                                    </div>
                                )}
                            </div>

                            <div className={styles.modalFooter}>
                                <button
                                    className={styles.secondaryActionBtn}
                                    onClick={() => setAiTestModal(prev => ({ ...prev, isOpen: false }))}
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* MODAL: LINK DYNAMIC QUERY TO THE AI ASSISTANT. */}
                {saveAiModalOpen && (
                    <div className={styles.modalOverlay} onClick={() => setSaveAiModalOpen(false)}>
                        <div className={styles.modalCard} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '680px' }}>
                            <form onSubmit={handleSaveAiRuleSubmit}>
                                <div className={styles.modalHeader}>
                                    <div className={styles.modalTitleBox}>
                                        <FaRobot style={{ color: 'var(--color-primary)' }} />
                                        <h3 className={styles.modalTitle}>
                                            {builderAiContext && !builderAiContext.isNew
                                                ? 'Actualizar Regla de Acceso IA desde el Constructor'
                                                : 'Vincular Consulta Dinámica al Asistente IA'}
                                        </h3>
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.closeBtn}
                                        onClick={() => setSaveAiModalOpen(false)}
                                    >
                                        <FaTimes />
                                    </button>
                                </div>

                                <div className={styles.modalBody}>
                                    {/* Summary of the visually generated query. */}
                                    <div className={styles.querySummaryCard}>
                                        <div className={styles.querySummaryItem}>
                                            <span className={styles.querySummaryLabel}>Origen de Datos:</span>
                                            <span className={styles.querySummaryBadge}>
                                                <FaDatabase size={11} /> {builder.selectedDb}
                                            </span>
                                            <span className={styles.querySummaryBadge}>
                                                <FaTable size={11} /> {builder.selectedTable}
                                            </span>
                                        </div>

                                        <div className={styles.querySummaryItem}>
                                            <span className={styles.querySummaryLabel}>Columnas ({builder.selectedColumns.length > 0 ? builder.selectedColumns.length : 'Todas'}):</span>
                                            {builder.selectedColumns.length > 0 ? (
                                                builder.selectedColumns.map(col => (
                                                    <span key={col} className={styles.querySummaryChip}>{col}</span>
                                                ))
                                            ) : (
                                                <span className={styles.querySummaryChip}>* (Todas las columnas disponibles)</span>
                                            )}
                                        </div>

                                        <div className={styles.querySummaryItem}>
                                            <span className={styles.querySummaryLabel}>Filtros WHERE:</span>
                                            {builder.filters.length > 0 && builder.filters.some(f => f.column) ? (
                                                builder.filters.filter(f => f.column).map(f => (
                                                    <span key={f.id} className={styles.querySummaryChip}>
                                                        {f.column} {f.operator} {f.value !== undefined ? String(f.value) : ''}
                                                    </span>
                                                ))
                                            ) : (
                                                <span style={{ fontSize: '0.8rem', color: '#6B7280' }}>
                                                    Sin filtros WHERE (Se consultan todos los registros)
                                                </span>
                                            )}
                                        </div>

                                        <div className={styles.querySummaryItem}>
                                            <span className={styles.querySummaryLabel}>Límite por Consulta:</span>
                                            <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151' }}>
                                                {builder.limit || 100} registros máximos para la IA
                                            </span>
                                        </div>
                                    </div>

                                    {/* AI configuration fields. */}
                                    <div className={styles.formGroup}>
                                        <label>Nombre Descriptivo</label>
                                        <input
                                            type="text"
                                            className={styles.selectInput}
                                            required
                                            value={saveAiForm.name}
                                            onChange={(e) => setSaveAiForm({ ...saveAiForm, name: e.target.value })}
                                            placeholder="ej. Certificados Oficiales Emitidos"
                                        />
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label>Código Identificador (RAG para la IA)</label>
                                        <input
                                            type="text"
                                            className={styles.selectInput}
                                            required
                                            disabled={!!(builderAiContext && !builderAiContext.isNew)}
                                            value={saveAiForm.code}
                                            onChange={(e) => setSaveAiForm({ ...saveAiForm, code: e.target.value })}
                                            placeholder="ej. emitted_certificates"
                                        />
                                        <span style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '3px', display: 'block' }}>
                                            Identificador único con el que el microservicio de IA indexa y busca este contexto.
                                        </span>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label>Descripción del Conjunto de Datos</label>
                                        <textarea
                                            className={styles.textareaInput}
                                            rows={2}
                                            value={saveAiForm.description}
                                            onChange={(e) => setSaveAiForm({ ...saveAiForm, description: e.target.value })}
                                            placeholder="Indica qué información aporta este dataset al asistente de IA en el chat..."
                                        />
                                    </div>

                                    {/* Interactive AI enable/disable toggle. */}
                                    <div className={styles.aiToggleRow}>
                                        <label className={styles.aiToggleLabel}>
                                            Estado de Acceso para el Asistente IA (Chat)
                                        </label>
                                        {saveAiForm.is_enabled ? (
                                            <button
                                                type="button"
                                                className={styles.aiToggleBtnActive}
                                                onClick={() => setSaveAiForm(f => ({ ...f, is_enabled: false }))}
                                                title="Haz clic para deshabilitar este contexto"
                                            >
                                                <FaToggleOn size={26} />
                                                <div>
                                                    <div>Habilitado para la IA</div>
                                                    <span style={{ fontSize: '0.75rem', fontWeight: 400, opacity: 0.9 }}>
                                                        El Asistente IA leerá estos datos en tiempo real para responder preguntas ciudadanas en el chat.
                                                    </span>
                                                </div>
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                className={styles.aiToggleBtnInactive}
                                                onClick={() => setSaveAiForm(f => ({ ...f, is_enabled: true }))}
                                                title="Haz clic para habilitar este contexto"
                                            >
                                                <FaToggleOff size={26} />
                                                <div>
                                                    <div>Bloqueado / Deshabilitado</div>
                                                    <span style={{ fontSize: '0.75rem', fontWeight: 400, opacity: 0.9 }}>
                                                        El Asistente IA NO tendrá acceso a estos datos en el chat hasta que sea habilitado.
                                                    </span>
                                                </div>
                                            </button>
                                        )}
                                        <span className={styles.aiToggleHint}>
                                            Podrás activar o desactivar este acceso en cualquier momento con un clic desde la pestaña "Control de Acceso IA".
                                        </span>
                                    </div>
                                </div>

                                <div className={styles.modalFooter}>
                                    <button
                                        type="button"
                                        className={styles.secondaryActionBtn}
                                        onClick={() => setSaveAiModalOpen(false)}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        className={styles.actionBtnPrimary}
                                        disabled={saveAiLoading}
                                    >
                                        {saveAiLoading ? (
                                            <><FaSpinner className={styles.spinIcon} /> Guardando...</>
                                        ) : (
                                            <><FaRobot /> Guardar Configuración de Consulta para que Consulte la IA</>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                
                {/* MODAL 4: AI QUERY AUDIT LOG. */}
                
                {auditModalOpen && (
                    <div className={styles.modalOverlay} onClick={() => setAuditModalOpen(false)}>
                        <div className={styles.modalCard} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '880px' }}>
                            <div className={styles.modalHeader}>
                                <div className={styles.modalTitleBox}>
                                    <FaHistory style={{ color: 'var(--color-primary)' }} />
                                    <h3 className={styles.modalTitle}>
                                        Bitácora de Auditoría: Consultas Ejecutadas por la IA
                                    </h3>
                                    <span className={styles.modalBadge}>datamart_db.ai_query_audit_logs</span>
                                </div>
                                <button
                                    className={styles.closeBtn}
                                    onClick={() => setAuditModalOpen(false)}
                                >
                                    <FaTimes />
                                </button>
                            </div>

                            <div className={styles.modalBody}>
                                {auditLogs.length === 0 ? (
                                    <div className={styles.emptyState}>
                                        <p>No hay registros en la bitácora aún.</p>
                                    </div>
                                ) : (
                                    <div className={styles.tableWrapper}>
                                        <table className={styles.dynamicTable}>
                                            <thead>
                                                <tr>
                                                    <th>Fecha / Hora</th>
                                                    <th>Consulta</th>
                                                    <th>Servicio</th>
                                                    <th>Filas</th>
                                                    <th>Tiempo (ms)</th>
                                                    <th>Estatus</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {auditLogs.map(log => (
                                                    <tr key={log.id}>
                                                        <td>{log.queried_at}</td>
                                                        <td><code>{log.query_code}</code></td>
                                                        <td>{log.client_service}</td>
                                                        <td>{log.row_count}</td>
                                                        <td>{log.execution_time_ms} ms</td>
                                                        <td>
                                                            {log.status === 'SUCCESS' ? (
                                                                <span className={styles.badgeNotNull} style={{ background: '#D1FAE5', color: '#065F46' }}>
                                                                    SUCCESS
                                                                </span>
                                                            ) : log.status === 'BLOCKED_DISABLED' ? (
                                                                <span className={styles.badgeNotNull} style={{ background: '#FEE2E2', color: '#991B1B' }}>
                                                                    BLOCKED
                                                                </span>
                                                            ) : (
                                                                <span className={styles.badgeNotNull} style={{ background: '#FEF3C7', color: '#92400E' }}>
                                                                    {log.status}
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>

                            <div className={styles.modalFooter}>
                                <button
                                    className={styles.secondaryActionBtn}
                                    onClick={() => setAuditModalOpen(false)}
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
}

export default Datamart;
